import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import { FaWhatsapp, FaEnvelope, FaQrcode, FaUsers, FaIdCard, FaCog, FaSignOutAlt, FaUserPlus, FaKey, FaTrash, FaClipboardList, FaFileAlt, FaCalendarAlt, FaEdit, FaPlus, FaCamera } from 'react-icons/fa';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setUser(res.data);
      }).catch(() => {
        localStorage.removeItem('token');
        setToken(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => React.useContext(AuthContext);

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800"><div className="text-white text-2xl">Caricamento...</div></div>;
  }
  
  return user ? children : <Navigate to="/login" state={{ from: location }} replace />;
};


// Protected Route con controllo ruolo
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800"><div className="text-white text-2xl">Caricamento...</div></div>;
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Controlla se l'utente ha il ruolo permesso
  if (!allowedRoles.includes(user.ruolo)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-500 to-red-700">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">🚫 Accesso Negato</h2>
          <p className="text-gray-700 mb-4">Non hai i permessi necessari per accedere a questa pagina.</p>
          <button 
            onClick={() => navigate('/')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};


// Login Page
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      login(response.data.access_token, response.data.user);
      
      // Reindirizza alla pagina originale o alla home
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center gap-4 mb-6">
              <img src="/images/logo-caf.png" alt="CAF" className="h-16 w-auto object-contain" />
              <img src="/images/logo-confintesa.png" alt="CONFINTESA" className="h-16 w-auto object-contain" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800">Accedi al Sistema</h2>
            <p className="text-gray-600 mt-2">CAF Confintesa - Tessere Associative</p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Inserisci username"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Inserisci password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? 'Accesso...' : 'Accedi'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Home Page
const Home = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div></div>
          <div className="flex gap-4">
            {user?.ruolo === 'admin' && (
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-gray-100 font-semibold transition-colors"
              >
                <FaCog /> Impostazioni
              </button>
            )}
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold transition-colors"
            >
              <FaSignOutAlt /> Esci
            </button>
          </div>
        </div>

        <div className="text-center mb-12">
          <div className="flex justify-center items-center gap-6 mb-6">
            <img src="/images/logo-caf.png" alt="CAF e non solo" className="h-24 w-auto object-contain drop-shadow-xl" />
            <img src="/images/logo-confintesa.png" alt="CONFINTESA" className="h-24 w-auto object-contain drop-shadow-xl" />
          </div>
          <h3 className="text-white text-2xl font-semibold mb-2">Sistema Tessere Associative</h3>
          <p className="text-white/80 text-lg">
            Benvenuto, {user?.username}! 
            ({user?.ruolo === 'admin' ? 'Amministratore' : user?.ruolo === 'operatore_completo' ? 'Operatore Completo' : 'Operatore Base'})
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          <button onClick={() => navigate('/register')} className="bg-white hover:bg-gray-50 p-8 rounded-xl shadow-xl transition-all transform hover:scale-105">
            <FaIdCard className="text-5xl text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Nuova Registrazione</h3>
            <p className="text-gray-600 text-sm">Registra un nuovo associato</p>
          </button>

          <button onClick={() => navigate('/scan')} className="bg-white hover:bg-gray-50 p-8 rounded-xl shadow-xl transition-all transform hover:scale-105">
            <FaQrcode className="text-5xl text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Scansiona QR</h3>
            <p className="text-gray-600 text-sm">Locale (PC/Mac)</p>
          </button>

          <button onClick={() => navigate('/scanner-remoto-qr')} className="bg-white hover:bg-gray-50 p-8 rounded-xl shadow-xl transition-all transform hover:scale-105 border-2 border-purple-300">
            <div className="text-5xl mx-auto mb-4">📱</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Scanner Remoto</h3>
            <p className="text-gray-600 text-sm">QR da Cellulare</p>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded mt-2 inline-block">DEMO</span>
          </button>

          <button onClick={() => navigate('/clients')} className="bg-white hover:bg-gray-50 p-8 rounded-xl shadow-xl transition-all transform hover:scale-105">
            <FaUsers className="text-5xl text-purple-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Lista Clienti</h3>
            <p className="text-gray-600 text-sm">Visualizza tutti i clienti</p>
          </button>
        </div>

        {/* Gestione Pratiche, Scansione OCR e Marketing - Solo per admin e operatore_completo */}
        {(user?.ruolo === 'admin' || user?.ruolo === 'operatore_completo') && (
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mt-6">
            <button onClick={() => navigate('/pratiche')} className="bg-white hover:bg-gray-50 p-8 rounded-xl shadow-xl transition-all transform hover:scale-105">
              <FaClipboardList className="text-5xl text-orange-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Gestione Pratiche</h3>
              <p className="text-gray-600 text-sm">Visualizza e gestisci tutte le pratiche</p>
            </button>

            <button onClick={() => navigate('/scansione')} className="bg-white hover:bg-gray-50 p-8 rounded-xl shadow-xl transition-all transform hover:scale-105">
              <FaCamera className="text-5xl text-teal-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Scansione OCR</h3>
              <p className="text-gray-600 text-sm">Locale (PC/Mac)</p>
            </button>
            
            <button onClick={() => navigate('/scanner-remoto-ocr')} className="bg-white hover:bg-gray-50 p-8 rounded-xl shadow-xl transition-all transform hover:scale-105 border-2 border-teal-300">
              <div className="text-5xl mx-auto mb-4">📄</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">OCR Remoto</h3>
              <p className="text-gray-600 text-sm">Foto da Cellulare</p>
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded mt-2 inline-block">NUOVO</span>
            </button>

            {/* Marketing solo per admin */}
            {user?.ruolo === 'admin' && (
              <button onClick={() => navigate('/marketing')} className="bg-white hover:bg-gray-50 p-8 rounded-xl shadow-xl transition-all transform hover:scale-105">
                <FaEnvelope className="text-5xl text-pink-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Marketing</h3>
                <p className="text-gray-600 text-sm">Invio email di massa ai clienti</p>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Registration Form with ALL fields
const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    codice_fiscale: '',
    sesso: 'M',
    data_nascita: '',
    luogo_nascita: '',
    stato_civile: 'Celibe/Nubile',
    numero_figli: '0',
    eta_figli: '',
    occupazione: 'Impiegato',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    telefono: '',
    email: '',
    documento_tipo: 'Carta d\'Identità',
    documento_numero: '',
    documento_data_emissione: '',
    documento_data_scadenza: '',
    data_scadenza_tessera: ''
  });
  const [etaFigliArray, setEtaFigliArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documentoScansionatoId, setDocumentoScansionatoId] = useState(null);

  // Pre-compila form con dati da query params (da scansione OCR)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const prefilledData = {};
    
    // Salva documento_scansionato_id se presente
    if (searchParams.has('documento_scansionato_id')) {
      setDocumentoScansionatoId(searchParams.get('documento_scansionato_id'));
    }
    
    // Dati anagrafici
    if (searchParams.has('nome')) prefilledData.nome = searchParams.get('nome');
    if (searchParams.has('cognome')) prefilledData.cognome = searchParams.get('cognome');
    if (searchParams.has('codice_fiscale')) prefilledData.codice_fiscale = searchParams.get('codice_fiscale');
    if (searchParams.has('data_nascita')) {
      const dataNascita = searchParams.get('data_nascita');
      // Converti formato da GG/MM/AAAA a AAAA-MM-GG se necessario
      if (dataNascita.includes('/')) {
        const [day, month, year] = dataNascita.split('/');
        prefilledData.data_nascita = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else {
        prefilledData.data_nascita = dataNascita;
      }
    }
    if (searchParams.has('luogo_nascita')) prefilledData.luogo_nascita = searchParams.get('luogo_nascita');
    if (searchParams.has('sesso')) {
      const sessoValue = searchParams.get('sesso');
      // Normalizza valori sesso per compatibilità con select
      if (sessoValue === 'M' || sessoValue === 'Maschio' || sessoValue === 'MASCHIO' || sessoValue === 'm') {
        prefilledData.sesso = 'M';
      } else if (sessoValue === 'F' || sessoValue === 'Femmina' || sessoValue === 'FEMMINA' || sessoValue === 'f') {
        prefilledData.sesso = 'F';
      } else {
        prefilledData.sesso = 'Altro';
      }
    }
    
    // Residenza
    if (searchParams.has('indirizzo')) prefilledData.indirizzo = searchParams.get('indirizzo');
    if (searchParams.has('citta')) prefilledData.citta = searchParams.get('citta');
    if (searchParams.has('cap')) prefilledData.cap = searchParams.get('cap');
    if (searchParams.has('provincia')) prefilledData.provincia = searchParams.get('provincia');
    
    // Documento
    if (searchParams.has('documento_tipo')) prefilledData.documento_tipo = searchParams.get('documento_tipo');
    if (searchParams.has('documento_numero')) prefilledData.documento_numero = searchParams.get('documento_numero');
    if (searchParams.has('documento_data_emissione')) {
      const dataEmissione = searchParams.get('documento_data_emissione');
      if (dataEmissione.includes('/')) {
        const [day, month, year] = dataEmissione.split('/');
        prefilledData.documento_data_emissione = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else {
        prefilledData.documento_data_emissione = dataEmissione;
      }
    }
    if (searchParams.has('documento_data_scadenza')) {
      const dataScadenza = searchParams.get('documento_data_scadenza');
      if (dataScadenza.includes('/')) {
        const [day, month, year] = dataScadenza.split('/');
        prefilledData.documento_data_scadenza = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else {
        prefilledData.documento_data_scadenza = dataScadenza;
      }
    }
    
    if (Object.keys(prefilledData).length > 0) {
      console.log('📝 Pre-filling form with OCR data:', prefilledData);
      setFormData(prev => ({ ...prev, ...prefilledData }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Aggiorna array età figli quando cambia il numero
    if (name === 'numero_figli') {
      const num = parseInt(value) || 0;
      setEtaFigliArray(Array(num).fill(''));
    }
  };

  const handleEtaFiglioChange = (index, value) => {
    const newArray = [...etaFigliArray];
    newArray[index] = value;
    setEtaFigliArray(newArray);
    setFormData({ ...formData, eta_figli: newArray.join(', ') });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/clients`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const clienteId = response.data.id;
      
      // Se c'è un documento scansionato, collegalo al cliente appena creato
      if (documentoScansionatoId) {
        try {
          await axios.put(
            `${API}/documenti/scansionati/${documentoScansionatoId}`,
            {
              dati_estratti: {}, // Mantieni i dati esistenti
              verificato: true,
              cliente_id: clienteId,
              note: 'Documento utilizzato per creazione cliente'
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
        } catch (linkErr) {
          console.error('Errore collegamento documento:', linkErr);
          // Non bloccare la creazione del cliente se il collegamento fallisce
        }
      }
      
      navigate(`/success/${clienteId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Registrazione Nuovo Associato</h2>
            <p className="text-gray-600">Compila tutti i campi per creare la tessera associativa</p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Dati Anagrafici</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nome *</label>
                  <input type="text" name="nome" required value={formData.nome} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cognome *</label>
                  <input type="text" name="cognome" required value={formData.cognome} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Codice Fiscale *</label>
                  <input type="text" name="codice_fiscale" required maxLength="16" value={formData.codice_fiscale} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sesso *</label>
                  <select name="sesso" required value={formData.sesso} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="M">Maschio</option>
                    <option value="F">Femmina</option>
                    <option value="Altro">Altro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data di Nascita *</label>
                  <input type="date" name="data_nascita" required value={formData.data_nascita} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Luogo di Nascita *</label>
                  <input type="text" name="luogo_nascita" required value={formData.luogo_nascita} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Stato Civile *</label>
                  <select name="stato_civile" required value={formData.stato_civile} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option>Celibe/Nubile</option>
                    <option>Coniugato/a</option>
                    <option>Separato/a</option>
                    <option>Divorziato/a</option>
                    <option>Vedovo/a</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Numero Figli *</label>
                  <input type="number" name="numero_figli" required min="0" max="20" value={formData.numero_figli} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                {parseInt(formData.numero_figli) > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Età dei Figli</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {etaFigliArray.map((eta, index) => (
                        <div key={index}>
                          <label className="block text-xs text-gray-600 mb-1">Figlio {index + 1}</label>
                          <select
                            value={eta}
                            onChange={(e) => handleEtaFiglioChange(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">Seleziona</option>
                            {[...Array(100)].map((_, i) => (
                              <option key={i} value={i}>{i} {i === 1 ? 'anno' : 'anni'}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Seleziona l'età di ogni figlio dal menu</p>
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Occupazione *</label>
                  <select name="occupazione" required value={formData.occupazione} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option>Impiegato</option>
                    <option>Operaio</option>
                    <option>Artigiano</option>
                    <option>Commerciante</option>
                    <option>Libero Professionista</option>
                    <option>Insegnante</option>
                    <option>Infermiere</option>
                    <option>Medico</option>
                    <option>Avvocato</option>
                    <option>Ingegnere</option>
                    <option>Architetto</option>
                    <option>Pensionato</option>
                    <option>Casalinga/o</option>
                    <option>Studente</option>
                    <option>Disoccupato</option>
                    <option>Altro</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">📞 Contatti e Residenza</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefono *</label>
                  <input type="tel" name="telefono" required value={formData.telefono} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Indirizzo *</label>
                  <input type="text" name="indirizzo" required value={formData.indirizzo} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Città *</label>
                  <input type="text" name="citta" required value={formData.citta} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">CAP *</label>
                  <input type="text" name="cap" required maxLength="5" value={formData.cap} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Provincia *</label>
                  <input type="text" name="provincia" required maxLength="2" value={formData.provincia} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase" />
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">🪪 Documento</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo Documento *</label>
                  <select name="documento_tipo" required value={formData.documento_tipo} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option>Carta d'Identità</option>
                    <option>Patente</option>
                    <option>Passaporto</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Numero Documento *</label>
                  <input type="text" name="documento_numero" required value={formData.documento_numero} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data Emissione *</label>
                  <input type="date" name="documento_data_emissione" required value={formData.documento_data_emissione} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data Scadenza Documento *</label>
                  <input type="date" name="documento_data_scadenza" required value={formData.documento_data_scadenza} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">🎫 Tessera Associativa</h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Data Scadenza Tessera *</label>
                <input type="date" name="data_scadenza_tessera" required value={formData.data_scadenza_tessera} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                <p className="text-sm text-gray-500 mt-1">Inserisci la data di scadenza della tessera</p>
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <button type="button" onClick={() => navigate('/')} className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-colors">
                Annulla
              </button>
              <button type="submit" disabled={loading} className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50">
                {loading ? 'Registrazione...' : 'Registra Associato'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit Client Component
const EditClient = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    codice_fiscale: '',
    sesso: 'M',
    data_nascita: '',
    luogo_nascita: '',
    stato_civile: 'Celibe/Nubile',
    numero_figli: '0',
    eta_figli: '',
    occupazione: 'Impiegato',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    telefono: '',
    email: '',
    documento_tipo: 'Carta d\'Identità',
    documento_numero: '',
    documento_data_emissione: '',
    documento_data_scadenza: '',
    data_scadenza_tessera: ''
  });
  const [etaFigliArray, setEtaFigliArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await axios.get(`${API}/clients/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const client = response.data;
        
        // Converti data_scadenza in formato YYYY-MM-DD per l'input
        const dataScadenza = new Date(client.data_scadenza);
        const dataScadenzaFormatted = dataScadenza.toISOString().split('T')[0];
        
        // Popola il form con i dati del cliente
        setFormData({
          nome: client.nome || '',
          cognome: client.cognome || '',
          codice_fiscale: client.codice_fiscale || '',
          sesso: client.sesso || 'M',
          data_nascita: client.data_nascita || '',
          luogo_nascita: client.luogo_nascita || '',
          stato_civile: client.stato_civile || 'Celibe/Nubile',
          numero_figli: String(client.numero_figli || 0),
          eta_figli: client.eta_figli || '',
          occupazione: client.occupazione || 'Impiegato',
          indirizzo: client.indirizzo || '',
          citta: client.citta || '',
          cap: client.cap || '',
          provincia: client.provincia || '',
          telefono: client.telefono || '',
          email: client.email || '',
          documento_tipo: client.documento_tipo || 'Carta d\'Identità',
          documento_numero: client.documento_numero || '',
          documento_data_emissione: client.documento_data_emissione || '',
          documento_data_scadenza: client.documento_data_scadenza || '',
          data_scadenza_tessera: dataScadenzaFormatted
        });
        
        // Popola l'array età figli se presente
        if (client.eta_figli && client.numero_figli > 0) {
          const etaArray = client.eta_figli.split(', ').map(eta => eta.trim());
          setEtaFigliArray(etaArray);
        } else if (client.numero_figli > 0) {
          setEtaFigliArray(Array(client.numero_figli).fill(''));
        }
        
      } catch (err) {
        console.error('Error fetching client:', err);
        setError('Errore nel caricamento dei dati del cliente');
      } finally {
        setLoadingData(false);
      }
    };
    fetchClient();
  }, [clientId, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Aggiorna array età figli quando cambia il numero
    if (name === 'numero_figli') {
      const num = parseInt(value) || 0;
      setEtaFigliArray(Array(num).fill(''));
    }
  };

  const handleEtaFiglioChange = (index, value) => {
    const newArray = [...etaFigliArray];
    newArray[index] = value;
    setEtaFigliArray(newArray);
    setFormData({ ...formData, eta_figli: newArray.join(', ') });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`${API}/clients/${clientId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/client/${clientId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante l\'aggiornamento');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-2xl">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Modifica Dati Cliente</h2>
            <p className="text-gray-600">Aggiorna le informazioni dell'associato</p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Dati Anagrafici</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nome *</label>
                  <input type="text" name="nome" required value={formData.nome} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cognome *</label>
                  <input type="text" name="cognome" required value={formData.cognome} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Codice Fiscale *</label>
                  <input type="text" name="codice_fiscale" required maxLength="16" value={formData.codice_fiscale} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sesso *</label>
                  <select name="sesso" required value={formData.sesso} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="M">Maschio</option>
                    <option value="F">Femmina</option>
                    <option value="Altro">Altro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data di Nascita *</label>
                  <input type="date" name="data_nascita" required value={formData.data_nascita} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Luogo di Nascita *</label>
                  <input type="text" name="luogo_nascita" required value={formData.luogo_nascita} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Stato Civile *</label>
                  <select name="stato_civile" required value={formData.stato_civile} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option>Celibe/Nubile</option>
                    <option>Coniugato/a</option>
                    <option>Separato/a</option>
                    <option>Divorziato/a</option>
                    <option>Vedovo/a</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Numero Figli *</label>
                  <input type="number" name="numero_figli" required min="0" max="20" value={formData.numero_figli} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                {parseInt(formData.numero_figli) > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Età dei Figli</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {etaFigliArray.map((eta, index) => (
                        <div key={index}>
                          <label className="block text-xs text-gray-600 mb-1">Figlio {index + 1}</label>
                          <select
                            value={eta}
                            onChange={(e) => handleEtaFiglioChange(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">Seleziona</option>
                            {[...Array(100)].map((_, i) => (
                              <option key={i} value={i}>{i} {i === 1 ? 'anno' : 'anni'}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Seleziona l'età di ogni figlio dal menu</p>
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Occupazione *</label>
                  <select name="occupazione" required value={formData.occupazione} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option>Impiegato</option>
                    <option>Operaio</option>
                    <option>Artigiano</option>
                    <option>Commerciante</option>
                    <option>Libero Professionista</option>
                    <option>Insegnante</option>
                    <option>Infermiere</option>
                    <option>Medico</option>
                    <option>Avvocato</option>
                    <option>Ingegnere</option>
                    <option>Architetto</option>
                    <option>Pensionato</option>
                    <option>Casalinga/o</option>
                    <option>Studente</option>
                    <option>Disoccupato</option>
                    <option>Altro</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">📞 Contatti e Residenza</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefono *</label>
                  <input type="tel" name="telefono" required value={formData.telefono} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Indirizzo *</label>
                  <input type="text" name="indirizzo" required value={formData.indirizzo} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Città *</label>
                  <input type="text" name="citta" required value={formData.citta} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">CAP *</label>
                  <input type="text" name="cap" required maxLength="5" value={formData.cap} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Provincia *</label>
                  <input type="text" name="provincia" required maxLength="2" value={formData.provincia} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase" />
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">🪪 Documento</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo Documento *</label>
                  <select name="documento_tipo" required value={formData.documento_tipo} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option>Carta d'Identità</option>
                    <option>Patente</option>
                    <option>Passaporto</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Numero Documento *</label>
                  <input type="text" name="documento_numero" required value={formData.documento_numero} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data Emissione *</label>
                  <input type="date" name="documento_data_emissione" required value={formData.documento_data_emissione} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data Scadenza Documento *</label>
                  <input type="date" name="documento_data_scadenza" required value={formData.documento_data_scadenza} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold text-gray-800 mb-4">🎫 Tessera Associativa</h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Data Scadenza Tessera *</label>
                <input type="date" name="data_scadenza_tessera" required value={formData.data_scadenza_tessera} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                <p className="text-sm text-gray-500 mt-1">Modifica la data di scadenza della tessera se necessario</p>
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <button type="button" onClick={() => navigate(`/client/${clientId}`)} className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-colors">
                Annulla
              </button>
              <button type="submit" disabled={loading} className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50">
                {loading ? 'Aggiornamento...' : 'Salva Modifiche'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Success Page with Share Options
const Success = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  // USA IL NUOVO URL BREVE!
  const cardUrl = client ? `${window.location.origin}/t/${client.short_id}` : '';

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await axios.get(`${API}/clients/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClient(response.data);
      } catch (err) {
        console.error('Error fetching client:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [clientId, token]);

  const shareWhatsApp = () => {
    const message = `Ecco la tua Tessera Associativa CAF Confintesa! 🎉\n\nVisualizza e salva la tua tessera:\n${cardUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const shareEmail = async () => {
    if (!client?.email) {
      alert('Email del cliente non disponibile');
      return;
    }
    
    const confirmSend = window.confirm(`Inviare la tessera via email a ${client.email}?`);
    if (!confirmSend) return;
    
    try {
      const response = await fetch(`${API}/send-card-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_email: client.email,
          client_name: `${client.nome} ${client.cognome}`,
          card_url: cardUrl,
          numero_tessera: client.numero_tessera
        })
      });
      
      if (response.ok) {
        alert('✅ Email inviata con successo!');
      } else {
        const error = await response.json();
        alert(`❌ Errore invio email: ${error.detail || 'Errore sconosciuto'}`);
      }
    } catch (error) {
      console.error('Errore invio email:', error);
      alert('❌ Errore durante l\'invio dell\'email. Riprova.');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(cardUrl);
    alert('Link copiato negli appunti!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-2xl">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-2xl p-8 mb-6">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Registrazione Completata!</h2>
              <p className="text-gray-600">La tessera associativa è stata creata con successo</p>
            </div>

            {client && (
              <div className="border-t pt-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Dati Associato</h3>
                <div className="space-y-2 text-gray-700">
                  <p><span className="font-semibold">Nome:</span> {client.nome} {client.cognome}</p>
                  <p><span className="font-semibold">Numero Tessera:</span> {client.numero_tessera}</p>
                  <p><span className="font-semibold">Email:</span> {client.email}</p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-2xl p-8 mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Condividi la Tessera</h3>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-600 mb-2">Link della tessera:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cardUrl}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                />
                <button
                  onClick={copyLink}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-semibold transition-colors"
                >
                  Copia
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={shareWhatsApp}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
              >
                <FaWhatsapp className="text-2xl" />
                Invia su WhatsApp
              </button>

              <button
                onClick={shareEmail}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
              >
                <FaEnvelope className="text-2xl" />
                Invia via Email
              </button>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => navigate(`/card/${clientId}?view=card`)}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors text-lg"
            >
              👁️ Visualizza Tessera
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
            >
              Torna alla Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// Membership Card Display PUBBLICA (via short_id) - SMART REDIRECT
// Membership Card Display PUBBLICA (via short_id) - SEMPRE TESSERA
const CardPublic = () => {
  const { shortId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await axios.get(`${API}/t/${shortId}`);
        setClient(response.data);
      } catch (err) {
        console.error('Error fetching client:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [shortId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-2xl text-gray-700">Caricamento tessera...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Tessera non trovata</h2>
          <p className="text-gray-600">Il link potrebbe essere scaduto o non valido</p>
        </div>
      </div>
    );
  }

  const formattedExpiry = new Date(client.data_scadenza).toLocaleDateString('it-IT');

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Tessera */}
        <div className="relative bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '400px' }}>
          <div className="p-6 flex flex-col h-full">
            {/* Header con loghi */}
            <div className="flex justify-between items-center mb-6">
              <img 
                src="/images/logo-caf.png" 
                alt="CAF" 
                className="h-20 w-auto object-contain drop-shadow-lg"
              />
              <img 
                src="/images/logo-confintesa.png" 
                alt="CONFINTESA" 
                className="h-20 w-auto object-contain drop-shadow-lg"
              />
            </div>

            {/* Titolo */}
            <h3 className="text-2xl font-semibold text-white text-center mb-6">Tessera Associativa</h3>
            
            {/* Contenuto */}
            <div className="flex items-start justify-between gap-6 flex-1">
              <div className="flex-1 space-y-3 text-white">
                <p className="text-xl">Nome: <span className="font-normal">{client.nome}</span></p>
                <p className="text-xl">Cognome: <span className="font-normal">{client.cognome}</span></p>
                <p className="text-xl font-bold pt-2">Numero Tessera:<br/>{client.numero_tessera}</p>
                <p className="text-base pt-2">Data di Scadenza:<br/>{formattedExpiry}</p>
              </div>

              <div className="bg-white p-2 rounded-lg flex-shrink-0">
                <QRCodeSVG
                  value={`${window.location.origin}/qr/${client.id}`}
                  size={130}
                  level="H"
                  className="block"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 text-center text-gray-600 text-sm">
          <p>Tessera Associativa CAF Confintesa</p>
          <p className="mt-2">Per assistenza contattaci</p>
        </div>
      </div>
    </div>
  );
};

// QR Redirect Component - redirects to full client details
const QRRedirect = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect immediately to client details
    navigate(`/client/${clientId}`, { replace: true });
  }, [clientId, navigate]);
  
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="text-2xl text-gray-700">Reindirizzamento...</div>
    </div>
  );
};

// Membership Card Display
const Card = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await axios.get(`${API}/clients/${clientId}`);
        setClient(response.data);
      } catch (err) {
        console.error('Error fetching client:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [clientId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-2xl text-gray-700">Caricamento tessera...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-800">Tessera non trovata</h2>
        </div>
      </div>
    );
  }

  const expiryDate = new Date(client.data_scadenza);
  const formattedExpiry = `${expiryDate.getDate().toString().padStart(2, '0')}/${(expiryDate.getMonth() + 1).toString().padStart(2, '0')}/${expiryDate.getFullYear()}`;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back button - SOLO per operatore loggato */}
        {user && (
          <div className="mb-4 flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-white border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-semibold transition-colors shadow-md"
            >
              ← Indietro
            </button>
            <button
              onClick={() => navigate(`/client/${clientId}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors shadow-md"
            >
              📋 Vedi Dati Completi
            </button>
          </div>
        )}

        {/* Tessera */}
        <div className="relative bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '400px' }}>
          <div className="p-6 flex flex-col h-full">
            {/* Header con loghi */}
            <div className="flex justify-between items-center mb-6">
              <img 
                src="/images/logo-caf.png" 
                alt="CAF" 
                className="h-20 w-auto object-contain drop-shadow-lg"
              />
              <img 
                src="/images/logo-confintesa.png" 
                alt="CONFINTESA" 
                className="h-20 w-auto object-contain drop-shadow-lg"
              />
            </div>

            {/* Titolo */}
            <h3 className="text-2xl font-semibold text-white text-center mb-6">Tessera Associativa</h3>
            
            {/* Contenuto */}
            <div className="flex items-start justify-between gap-6 flex-1">
              <div className="flex-1 space-y-3 text-white">
                <p className="text-xl">Nome: <span className="font-normal">{client.nome}</span></p>
                <p className="text-xl">Cognome: <span className="font-normal">{client.cognome}</span></p>
                <p className="text-xl font-bold pt-2">Numero Tessera:<br/>{client.numero_tessera}</p>
                <p className="text-base pt-2">Data di Scadenza:<br/>{formattedExpiry}</p>
              </div>

              <div className="bg-white p-2 rounded-lg flex-shrink-0">
                <QRCodeSVG
                  value={`${window.location.origin}/qr/${client.id}`}
                  size={130}
                  level="H"
                  className="block"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
// QR Scanner
const Scanner = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let html5QrCode;
    let isScanning = false;

    const startScanner = async () => {
      try {
        html5QrCode = new Html5Qrcode('reader');
        
        // Prima rileva le fotocamere disponibili
        const cameras = await Html5Qrcode.getCameras();
        
        if (!cameras || cameras.length === 0) {
          setError('Nessuna fotocamera trovata sul dispositivo.');
          return;
        }
        
        console.log('Fotocamere disponibili:', cameras);
        
        // Prova prima con la fotocamera posteriore
        let cameraId = cameras[cameras.length - 1].id; // Ultima camera (di solito posteriore)
        
        // Se c'è più di una camera, cerca quella posteriore
        const rearCamera = cameras.find(camera => 
          camera.label.toLowerCase().includes('back') || 
          camera.label.toLowerCase().includes('rear') ||
          camera.label.toLowerCase().includes('environment')
        );
        
        if (rearCamera) {
          cameraId = rearCamera.id;
        }
        
        console.log('Usando fotocamera:', cameraId);
        
        await html5QrCode.start(
          cameraId, // Usa l'ID specifico invece di facingMode
          {
            fps: 10,
            qrbox: { width: 250, height: 250 }
          },
          (decodedText) => {
            // Stop scanner first
            if (html5QrCode && isScanning) {
              html5QrCode.stop().then(() => {
                isScanning = false;
                // Extract client ID or shortId from URL
                let clientId = decodedText;
                
                // Nuovo formato QR: /qr/:clientId (per operatori)
                if (decodedText.includes('/qr/')) {
                  const parts = decodedText.split('/qr/');
                  clientId = parts[1].split('?')[0].split('#')[0];
                  navigate(`/qr/${clientId}`);
                  return;
                } else if (decodedText.includes('/t/')) {
                  // Link pubblico tessera
                  const shortId = decodedText.split('/t/')[1].split('?')[0].split('#')[0];
                  navigate(`/t/${shortId}`);
                  return;
                } else if (decodedText.includes('/card/')) {
                  const parts = decodedText.split('/card/');
                  clientId = parts[1].split('?')[0].split('#')[0];
                  navigate(`/card/${clientId}`);
                  return;
                } else if (decodedText.includes('/client/')) {
                  const parts = decodedText.split('/client/');
                  clientId = parts[1].split('?')[0].split('#')[0];
                }
                
                // Navigate to client details page
                navigate(`/client/${clientId}`);
              }).catch((err) => {
                console.error('Error stopping scanner:', err);
                // Navigate anyway
                let clientId = decodedText;
                if (decodedText.includes('/qr/')) {
                  clientId = decodedText.split('/qr/')[1].split('?')[0].split('#')[0];
                  navigate(`/qr/${clientId}`);
                  return;
                } else if (decodedText.includes('/t/')) {
                  const shortId = decodedText.split('/t/')[1].split('?')[0].split('#')[0];
                  navigate(`/t/${shortId}`);
                  return;
                } else if (decodedText.includes('/card/')) {
                  clientId = decodedText.split('/card/')[1].split('?')[0].split('#')[0];
                  navigate(`/card/${clientId}`);
                  return;
                }
                navigate(`/client/${clientId}`);
              });
            }
          },
          (errorMessage) => {
            // Ignore continuous scan errors
          }
        );
        
        isScanning = true;
        setScanning(true);
      } catch (err) {
        console.error('Error starting scanner:', err);
        if (err.name === 'NotAllowedError') {
          setError('Permesso fotocamera negato. Clicca sull\'icona 🔒 nella barra degli indirizzi e consenti l\'accesso alla fotocamera.');
        } else if (err.name === 'NotFoundError') {
          setError('Nessuna fotocamera trovata. Assicurati che il dispositivo abbia una fotocamera funzionante.');
        } else if (err.name === 'NotReadableError') {
          setError('Fotocamera già in uso da un\'altra applicazione. Chiudi le altre app e riprova.');
        } else {
          setError('Impossibile avviare la fotocamera: ' + err.message);
        }
      }
    };

    startScanner();

    return () => {
      if (html5QrCode && isScanning) {
        isScanning = false;
        html5QrCode.stop().catch((err) => {
          console.error('Cleanup error:', err);
        });
      }
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-2xl p-8 mb-6">
            <div className="text-center mb-6">
              <FaQrcode className="text-6xl text-blue-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Scansiona QR Code</h2>
              <p className="text-gray-600">Inquadra il QR code della tessera</p>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            <div id="reader" className="rounded-lg overflow-hidden mb-6"></div>

            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors"
            >
              Annulla
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// Clients List
const ClientsList = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClients();
  }, [token]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clientId, clientName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare ${clientName}?\n\nQuesta azione è irreversibile!`)) {
      return;
    }

    try {
      await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Rimuovi dalla lista
      setClients(clients.filter(c => c.id !== clientId));
      alert('Cliente eliminato con successo');
    } catch (err) {
      alert('Errore durante l\'eliminazione: ' + (err.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/clients/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `Clienti_CAF_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('✅ File Excel scaricato con successo!');
    } catch (err) {
      alert('Errore durante l\'esportazione: ' + (err.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const filteredClients = clients.filter(client =>
    client.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.cognome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.numero_tessera.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.codice_fiscale.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-2xl">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-2xl p-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Lista Clienti</h2>
                <p className="text-gray-600">{clients.length} associati registrati</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleExportExcel}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors flex items-center gap-2"
                >
                  📥 Esporta Excel
                </button>
                {user?.ruolo === 'admin' && (
                  <button
                    onClick={() => navigate('/marketing')}
                    className="px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-semibold transition-colors flex items-center gap-2"
                  >
                    📧 Marketing
                  </button>
                )}
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
                >
                  Home
                </button>
              </div>
            </div>

            <div className="mb-6">
              <input
                type="text"
                placeholder="Cerca per nome, cognome, tessera o codice fiscale..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Tessera</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Nome Completo</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Codice Fiscale</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Telefono</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Azioni</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredClients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{client.numero_tessera}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{client.nome} {client.cognome}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.codice_fiscale}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.telefono}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/client/${client.id}`)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition-colors text-xs"
                          >
                            📄 Dettagli
                          </button>
                          <button
                            onClick={() => navigate(`/card/${client.id}`)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors text-xs"
                          >
                            Tessera
                          </button>
                          {user?.ruolo === 'admin' && (
                            <button
                              onClick={() => handleDelete(client.id, `${client.nome} ${client.cognome}`)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors text-xs"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredClients.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  Nessun cliente trovato
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('password');
  
  // Change Password State
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Manage Users State (admin only)
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('operatore');
  const [userError, setUserError] = useState('');

  useEffect(() => {
    if (user?.ruolo === 'admin' && activeTab === 'users') {
      fetchUsers();
    }
  }, [user, activeTab, token]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Le password non corrispondono');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('La password deve essere almeno 6 caratteri');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPasswordSuccess('Password cambiata con successo!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Errore durante il cambio password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setUserError('');
    
    if (newUserPassword.length < 6) {
      setUserError('La password deve essere almeno 6 caratteri');
      return;
    }
    
    try {
      await axios.post(`${API}/users`, {
        username: newUsername,
        password: newUserPassword,
        ruolo: newUserRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowAddUser(false);
      setNewUsername('');
      setNewUserPassword('');
      setNewUserRole('operatore');
      fetchUsers();
    } catch (err) {
      setUserError(err.response?.data?.detail || 'Errore durante la creazione utente');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'utente ${username}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      alert('Errore durante l\'eliminazione: ' + (err.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-blue-600 text-white p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-2">⚙️ Impostazioni</h2>
                  <p className="text-blue-100">Gestisci il tuo account e il sistema</p>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-semibold transition-colors"
                >
                  Home
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('password')}
                  className={`px-6 py-4 font-semibold transition-colors ${
                    activeTab === 'password'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  🔐 Cambio Password
                </button>
                {user?.ruolo === 'admin' && (
                  <button
                    onClick={() => setActiveTab('users')}
                    className={`px-6 py-4 font-semibold transition-colors ${
                      activeTab === 'users'
                        ? 'border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    👥 Gestione Operatori
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Change Password Tab */}
              {activeTab === 'password' && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Cambia Password</h3>
                  
                  {passwordError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {passwordError}
                    </div>
                  )}
                  
                  {passwordSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                      {passwordSuccess}
                    </div>
                  )}

                  <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Password Attuale</label>
                      <input
                        type="password"
                        required
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nuova Password</label>
                      <input
                        type="password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Conferma Nuova Password</label>
                      <input
                        type="password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50"
                    >
                      {changingPassword ? 'Cambiando...' : 'Cambia Password'}
                    </button>
                  </form>
                </div>
              )}

              {/* Manage Users Tab (Admin Only) */}
              {activeTab === 'users' && user?.ruolo === 'admin' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-800">Gestione Operatori</h3>
                    <button
                      onClick={() => setShowAddUser(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors"
                    >
                      ➕ Aggiungi Operatore
                    </button>
                  </div>

                  {/* Add User Form */}
                  {showAddUser && (
                    <div className="bg-blue-50 p-6 rounded-lg mb-6">
                      <h4 className="text-xl font-bold text-gray-800 mb-4">Nuovo Operatore</h4>
                      
                      {userError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                          {userError}
                        </div>
                      )}

                      <form onSubmit={handleAddUser} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                            <input
                              type="text"
                              required
                              value={newUsername}
                              onChange={(e) => setNewUsername(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                            <input
                              type="password"
                              required
                              value={newUserPassword}
                              onChange={(e) => setNewUserPassword(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Ruolo</label>
                            <select
                              value={newUserRole}
                              onChange={(e) => setNewUserRole(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="operatore">Operatore</option>
                              <option value="admin">Admin</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddUser(false);
                              setUserError('');
                            }}
                            className="flex-1 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-colors"
                          >
                            Annulla
                          </button>
                          <button
                            type="submit"
                            className="flex-1 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors"
                          >
                            Crea Operatore
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Users List */}
                  {loadingUsers ? (
                    <div className="text-center py-8 text-gray-600">Caricamento...</div>
                  ) : (
                    <div className="space-y-4">
                      {users.map((u) => (
                        <div key={u.id} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-gray-900">{u.username}</p>
                            <p className="text-sm text-gray-600">
                              Ruolo: <span className="font-semibold">{u.ruolo}</span>
                            </p>
                          </div>
                          {u.username !== 'admin' && (
                            <button
                              onClick={() => handleDeleteUser(u.id, u.username)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors text-sm"
                            >
                              🗑️ Elimina
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Client - Component inserito dopo Settings - continua nel prossimo file per lunghezza

// Client Details Page
const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('📋 ClientDetails - Loading client ID:', clientId);
    const fetchClient = async () => {
      try {
        console.log('🔍 Fetching client from API:', `${API}/clients/${clientId}`);
        const response = await axios.get(`${API}/clients/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Client data received:', response.data);
        setClient(response.data);
      } catch (err) {
        console.error('❌ Error fetching client:', err);
        console.error('❌ Error details:', err.response?.data);
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [clientId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 shadow-2xl">
          <div className="text-blue-600 text-2xl font-bold mb-4">⏳ Caricamento...</div>
          <p className="text-gray-600">Recupero dati cliente ID: {clientId}</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 shadow-2xl text-center">
          <div className="text-red-600 text-3xl font-bold mb-4">❌ Cliente non trovato</div>
          <p className="text-gray-600 mb-4">ID cercato: {clientId}</p>
          <p className="text-gray-500 text-sm mb-6">Il cliente potrebbe non esistere o l'ID è errato</p>
          <button
            onClick={() => navigate('/clients')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            ← Torna alla Lista Clienti
          </button>
        </div>
      </div>
    );
  }

  const expiryDate = new Date(client.data_scadenza);
  const formattedExpiry = `${expiryDate.getDate().toString().padStart(2, '0')}/${(expiryDate.getMonth() + 1).toString().padStart(2, '0')}/${expiryDate.getFullYear()}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 flex gap-4">
            <button
              onClick={() => navigate('/clients')}
              className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-gray-100 font-semibold transition-colors"
            >
              ← Torna alla Lista
            </button>
            <button
              onClick={() => navigate(`/client/${clientId}/edit`)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold transition-colors"
            >
              ✏️ Modifica
            </button>
            <button
              onClick={() => navigate(`/card/${client.id}`)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors"
            >
              👁️ Visualizza Tessera
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Scheda Completa Cliente</h2>
              <p className="text-xl text-blue-600 font-semibold">Tessera N° {client.numero_tessera}</p>
            </div>

            <div className="space-y-6">
              {/* Dati Anagrafici */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  📋 Dati Anagrafici
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Nome</p>
                    <p className="text-lg font-semibold text-gray-900">{client.nome}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Cognome</p>
                    <p className="text-lg font-semibold text-gray-900">{client.cognome}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Codice Fiscale</p>
                    <p className="text-lg font-semibold text-gray-900">{client.codice_fiscale}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Data di Nascita</p>
                    <p className="text-lg font-semibold text-gray-900">{client.data_nascita}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Luogo di Nascita</p>
                    <p className="text-lg font-semibold text-gray-900">{client.luogo_nascita}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Stato Civile</p>
                    <p className="text-lg font-semibold text-gray-900">{client.stato_civile || 'N/D'}</p>
                  </div>
                  {client.numero_figli > 0 && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">Numero Figli</p>
                        <p className="text-lg font-semibold text-gray-900">{client.numero_figli}</p>
                      </div>
                      {client.eta_figli && (
                        <div>
                          <p className="text-sm text-gray-600">Età Figli</p>
                          <p className="text-lg font-semibold text-gray-900">{client.eta_figli}</p>
                        </div>
                      )}
                    </>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Occupazione</p>
                    <p className="text-lg font-semibold text-gray-900">{client.occupazione || 'N/D'}</p>
                  </div>
                </div>
              </div>

              {/* Contatti e Residenza */}
              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  📞 Contatti e Residenza
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Telefono</p>
                    <p className="text-lg font-semibold text-gray-900">{client.telefono}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-lg font-semibold text-gray-900">{client.email}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-600">Indirizzo Completo</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {client.indirizzo}, {client.cap} {client.citta} ({client.provincia})
                    </p>
                  </div>
                </div>
              </div>

              {/* Documento */}
              <div className="bg-yellow-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  🪪 Documento
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Tipo Documento</p>
                    <p className="text-lg font-semibold text-gray-900">{client.documento_tipo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Numero Documento</p>
                    <p className="text-lg font-semibold text-gray-900">{client.documento_numero}</p>
                  </div>
                  {client.documento_data_emissione && (
                    <div>
                      <p className="text-sm text-gray-600">Data Emissione</p>
                      <p className="text-lg font-semibold text-gray-900">{client.documento_data_emissione}</p>
                    </div>
                  )}
                  {client.documento_data_scadenza && (
                    <div>
                      <p className="text-sm text-gray-600">Data Scadenza</p>
                      <p className="text-lg font-semibold text-gray-900">{client.documento_data_scadenza}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tessera */}
              <div className="bg-purple-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  🎫 Tessera Associativa
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Numero Tessera</p>
                    <p className="text-2xl font-bold text-blue-600">{client.numero_tessera}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Data Scadenza</p>
                    <p className="text-lg font-semibold text-gray-900">{formattedExpiry}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Upload Documenti - Pagina PUBBLICA
const UploadDocumenti = () => {
  const { uploadToken } = useParams();
  const [praticaInfo, setPraticaInfo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPraticaInfo = async () => {
      try {
        const response = await axios.get(`${API}/upload/${uploadToken}`);
        setPraticaInfo(response.data);
      } catch (err) {
        setError('Link non valido o scaduto');
      }
    };
    fetchPraticaInfo();
  }, [uploadToken]);

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Seleziona un file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      await axios.post(`${API}/upload/${uploadToken}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
      setSelectedFile(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante caricamento');
    } finally {
      setUploading(false);
    }
  };

  if (!praticaInfo && !error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-2xl">Caricamento...</div>
      </div>
    );
  }

  if (error && !praticaInfo) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Errore</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-green-600 mb-4">Documento Caricato!</h2>
          <p className="text-gray-600 mb-6">Il documento è stato caricato con successo.</p>
          <button
            onClick={() => { setSuccess(false); setSelectedFile(null); }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            Carica Altro Documento
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Carica Documenti</h2>
            <p className="text-gray-600">CAF Confintesa</p>
          </div>

          <div className="bg-blue-50 p-6 rounded-lg mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Informazioni Pratica</h3>
            <div className="space-y-2">
              <p><strong>Cliente:</strong> {praticaInfo?.cliente_nome}</p>
              <p><strong>Tipo:</strong> {praticaInfo?.tipo_pratica}</p>
              <p><strong>Anno:</strong> {praticaInfo?.anno_fiscale}</p>
            </div>
          </div>
          
          {praticaInfo?.documento_richiesto && (
            <div className="bg-yellow-50 border-2 border-yellow-400 p-6 rounded-lg mb-8">
              <div className="flex items-start gap-3">
                <div className="text-3xl">📄</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">Documento Richiesto:</h3>
                  <p className="text-xl font-semibold text-blue-600">{praticaInfo.documento_richiesto}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Seleziona Documento
            </label>
            <input
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-sm text-gray-500 mt-2">
              Formati supportati: PDF, JPG, PNG, DOC, DOCX (max 10MB)
            </p>
          </div>

          {selectedFile && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-600">File selezionato:</p>
              <p className="font-semibold">{selectedFile.name}</p>
              <p className="text-sm text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Caricamento...' : 'Carica Documento'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Gestione Pratiche Component
const GestionePratiche = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [pratiche, setPratiche] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPratica, setEditingPratica] = useState(null);
  const [filtro, setFiltro] = useState('Tutte');
  const [formData, setFormData] = useState({
    cliente_id: '',
    tipo_pratica: '730',
    anno_fiscale: new Date().getFullYear(),
    stato: 'In Lavorazione',
    note: '',
    importo: 0,
    operatore: ''
  });

  const tipiPratica = ['730', 'ISEE', 'IMU', 'F24', 'Dichiarazione Redditi', 'Bonus/Agevolazioni', 'Partita IVA', 'Certificazioni', 'Altro'];
  const stati = ['In Lavorazione', 'Completata', 'Sospesa'];

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [praticheRes, clientiRes] = await Promise.all([
        axios.get(`${API}/pratiche`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPratiche(praticheRes.data);
      setClienti(clientiRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const cliente = clienti.find(c => c.id === formData.cliente_id);
      const praticaData = {
        ...formData,
        cliente_nome: `${cliente.nome} ${cliente.cognome}`,
        operatore: 'admin' // TODO: Get from auth context
      };

      if (editingPratica) {
        await axios.put(`${API}/pratiche/${editingPratica.id}`, praticaData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Pratica aggiornata!');
      } else {
        await axios.post(`${API}/pratiche`, praticaData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Pratica creata!');
      }
      
      setShowForm(false);
      setEditingPratica(null);
      resetForm();
      fetchData();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      tipo_pratica: '730',
      anno_fiscale: new Date().getFullYear(),
      stato: 'In Lavorazione',
      note: '',
      importo: 0,
      operatore: ''
    });
  };

  const handleEdit = (pratica) => {
    setEditingPratica(pratica);
    setFormData({
      cliente_id: pratica.cliente_id,
      tipo_pratica: pratica.tipo_pratica,
      anno_fiscale: pratica.anno_fiscale,
      stato: pratica.stato,
      note: pratica.note,
      importo: pratica.importo,
      operatore: pratica.operatore
    });
    setShowForm(true);
  };

  const handleDelete = async (praticaId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa pratica?')) return;
    
    try {
      await axios.delete(`${API}/pratiche/${praticaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Pratica eliminata!');
      fetchData();
    } catch (err) {
      alert('Errore durante eliminazione');
    }
  };

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDocumentoModal, setShowDocumentoModal] = useState(false);
  const [showDocumentiListModal, setShowDocumentiListModal] = useState(false);
  const [selectedPraticaId, setSelectedPraticaId] = useState(null);
  const [documentoRichiesto, setDocumentoRichiesto] = useState('');
  const [uploadLinkData, setUploadLinkData] = useState(null);
  const [documentiCaricati, setDocumentiCaricati] = useState([]);
  const [loadingDocumenti, setLoadingDocumenti] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState({}); // Store temp download links

  const handleVediDocumenti = async (praticaId) => {
    setSelectedPraticaId(praticaId);
    setLoadingDocumenti(true);
    setShowDocumentiListModal(true);
    setDownloadLinks({}); // Reset download links
    
    try {
      const response = await axios.get(`${API}/pratiche/${praticaId}/documenti`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentiCaricati(response.data);
      
      // Genera link temporanei per tutti i documenti
      const links = {};
      for (const doc of response.data) {
        try {
          const linkResponse = await axios.post(`${API}/documenti/${doc.id}/get-temp-link`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          links[doc.id] = `${window.location.origin}${linkResponse.data.download_url}`;
        } catch (err) {
          console.error('Errore generazione link:', err);
        }
      }
      setDownloadLinks(links);
    } catch (err) {
      console.error('Errore caricamento documenti:', err);
      setDocumentiCaricati([]);
    } finally {
      setLoadingDocumenti(false);
    }
  };

  const handleRichiediDocumenti = async (praticaId) => {
    // Apri modal per chiedere quale documento
    setSelectedPraticaId(praticaId);
    setDocumentoRichiesto('');
    setShowDocumentoModal(true);
  };

  const handleGeneraLink = async () => {
    if (!documentoRichiesto.trim()) {
      alert('Specifica quale documento ti serve');
      return;
    }

    try {
      console.log('Richiesta upload per pratica:', selectedPraticaId);
      
      const response = await axios.post(`${API}/pratiche/${selectedPraticaId}/request-upload`, {
        documento_richiesto: documentoRichiesto
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Response:', response.data);
      
      const uploadUrl = `${window.location.origin}/upload/${response.data.upload_token}`;
      
      // Prova a copiare negli appunti
      let copied = false;
      try {
        await navigator.clipboard.writeText(uploadUrl);
        copied = true;
        console.log('Link copiato negli appunti');
      } catch (clipboardErr) {
        console.log('Clipboard non disponibile:', clipboardErr);
      }
      
      // Chiudi modal documento e mostra modal link
      setShowDocumentoModal(false);
      setUploadLinkData({ url: uploadUrl, copied, documento: documentoRichiesto });
      setShowUploadModal(true);
    } catch (err) {
      console.error('Errore completo:', err);
      alert('Errore nella generazione del link: ' + (err.response?.data?.detail || err.message));
    }
  };

  const praticheFiltered = filtro === 'Tutte' 
    ? pratiche 
    : pratiche.filter(p => p.stato === filtro);

  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-2xl">Caricamento...</div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      {/* Modal Richiesta Documento */}
      {showDocumentoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDocumentoModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Richiedi Documento</h3>
              <p className="text-gray-600">Specifica quale documento ti serve dal cliente</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Documento Richiesto *
              </label>
              <input
                type="text"
                value={documentoRichiesto}
                onChange={(e) => setDocumentoRichiesto(e.target.value)}
                placeholder="Es: Carta d'Identità fronte e retro"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                💡 Esempi: CUD 2024, Ricevute mediche, Contratto di affitto, ecc.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleGeneraLink}
                disabled={!documentoRichiesto.trim()}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                📤 Genera Link
              </button>
              <button
                onClick={() => setShowDocumentoModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Upload Link */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowUploadModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Link Generato!</h3>
              <p className="text-gray-600">Invia questo link al cliente per caricare:</p>
              <p className="text-lg font-semibold text-blue-600 mt-2">{uploadLinkData?.documento}</p>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-700 break-all font-mono">{uploadLinkData?.url}</p>
            </div>
            
            {uploadLinkData?.copied && (
              <p className="text-green-600 text-center mb-4">
                📋 Link copiato negli appunti!
              </p>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(uploadLinkData?.url);
                  setUploadLinkData({...uploadLinkData, copied: true});
                }}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                📋 Copia Link
              </button>
              <button
                onClick={() => setShowUploadModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Lista Documenti Caricati */}
      {showDocumentiListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDocumentiListModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">📁</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Documenti Caricati</h3>
              <p className="text-gray-600">Documenti ricevuti dal cliente per questa pratica</p>
            </div>
            
            {loadingDocumenti ? (
              <div className="text-center py-8">
                <div className="text-2xl">Caricamento...</div>
              </div>
            ) : documentiCaricati.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg text-center">
                <p className="text-lg text-gray-700">📭 Nessun documento caricato ancora</p>
                <p className="text-sm text-gray-500 mt-2">Il cliente non ha ancora caricato documenti per questa pratica</p>
              </div>
            ) : (
              <div className="space-y-4">
                {documentiCaricati.map((doc, index) => (
                  <div key={doc.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex items-center justify-between hover:bg-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">
                        {doc.nome_file?.endsWith('.pdf') ? '📄' : 
                         doc.nome_file?.match(/\.(jpg|jpeg|png)$/i) ? '🖼️' : '📎'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{doc.nome_file}</p>
                        <p className="text-sm text-gray-500">
                          Caricato il {new Date(doc.data_upload).toLocaleDateString('it-IT')} alle {new Date(doc.data_upload).toLocaleTimeString('it-IT')}
                        </p>
                        {doc.tipo && <p className="text-xs text-gray-400 mt-1">{doc.tipo}</p>}
                      </div>
                    </div>
                    {downloadLinks[doc.id] ? (
                      <a
                        href={downloadLinks[doc.id]}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold inline-block"
                      >
                        ⬇️ Scarica
                      </a>
                    ) : (
                      <span className="px-4 py-2 bg-gray-300 text-gray-600 rounded-lg font-semibold">
                        ⏳ Caricamento...
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowDocumentiListModal(false)}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Gestione Pratiche</h2>
              <p className="text-gray-600">{pratiche.length} pratiche totali</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { resetForm(); setEditingPratica(null); setShowForm(!showForm); }} className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center gap-2">
                <FaPlus /> Nuova Pratica
              </button>
              <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                Home
              </button>
            </div>
          </div>

          {showForm && (
            <div className="mb-8 bg-blue-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-4">{editingPratica ? 'Modifica Pratica' : 'Nuova Pratica'}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Cliente *</label>
                    <select required value={formData.cliente_id} onChange={(e) => setFormData({...formData, cliente_id: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                      <option value="">Seleziona cliente</option>
                      {clienti.map(c => (
                        <option key={c.id} value={c.id}>{c.nome} {c.cognome} - {c.codice_fiscale}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo Pratica *</label>
                    <select required value={formData.tipo_pratica} onChange={(e) => setFormData({...formData, tipo_pratica: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                      {tipiPratica.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Anno Fiscale *</label>
                    <input type="number" required value={formData.anno_fiscale} onChange={(e) => setFormData({...formData, anno_fiscale: parseInt(e.target.value)})} className="w-full px-4 py-2 border border-gray-300 rounded-lg" min="2020" max="2030" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Stato *</label>
                    <select required value={formData.stato} onChange={(e) => setFormData({...formData, stato: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                      {stati.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Importo (€)</label>
                    <input type="number" step="0.01" value={formData.importo} onChange={(e) => setFormData({...formData, importo: parseFloat(e.target.value) || 0})} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Note</label>
                    <textarea value={formData.note} onChange={(e) => setFormData({...formData, note: e.target.value})} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg"></textarea>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                    {editingPratica ? 'Aggiorna' : 'Crea'} Pratica
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setEditingPratica(null); resetForm(); }} className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-semibold">
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="mb-6 flex gap-3">
            <button onClick={() => setFiltro('Tutte')} className={`px-4 py-2 rounded-lg font-semibold ${filtro === 'Tutte' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
              Tutte ({pratiche.length})
            </button>
            <button onClick={() => setFiltro('In Lavorazione')} className={`px-4 py-2 rounded-lg font-semibold ${filtro === 'In Lavorazione' ? 'bg-yellow-600 text-white' : 'bg-gray-200'}`}>
              In Lavorazione ({pratiche.filter(p => p.stato === 'In Lavorazione').length})
            </button>
            <button onClick={() => setFiltro('Completata')} className={`px-4 py-2 rounded-lg font-semibold ${filtro === 'Completata' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>
              Completate ({pratiche.filter(p => p.stato === 'Completata').length})
            </button>
            <button onClick={() => setFiltro('Sospesa')} className={`px-4 py-2 rounded-lg font-semibold ${filtro === 'Sospesa' ? 'bg-red-600 text-white' : 'bg-gray-200'}`}>
              Sospese ({pratiche.filter(p => p.stato === 'Sospesa').length})
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Anno</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Stato</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Data Apertura</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Importo</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {praticheFiltered.map((pratica) => (
                  <tr key={pratica.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{pratica.cliente_nome}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{pratica.tipo_pratica}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{pratica.anno_fiscale}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        pratica.stato === 'Completata' ? 'bg-green-100 text-green-800' :
                        pratica.stato === 'In Lavorazione' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {pratica.stato}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(pratica.data_apertura).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">€{pratica.importo?.toFixed(2) || '0.00'}</td>
                    <td className="px-6 py-4 text-sm space-x-2">
                      <button type="button" onClick={() => navigate(`/client/${pratica.cliente_id}`)} className="text-blue-600 hover:text-blue-800 font-semibold">
                        Vedi Cliente
                      </button>
                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleVediDocumenti(pratica.id); }} className="text-green-600 hover:text-green-800 font-semibold">
                        📄 Vedi Documenti
                      </button>
                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRichiediDocumenti(pratica.id); }} className="text-purple-600 hover:text-purple-800 font-semibold">
                        📤 Documenti
                      </button>
                      <button type="button" onClick={() => handleEdit(pratica)} className="text-orange-600 hover:text-orange-800 font-semibold">
                        Modifica
                      </button>
                      {user?.ruolo === 'admin' && (
                        <button type="button" onClick={() => handleDelete(pratica.id)} className="text-red-600 hover:text-red-800 font-semibold">
                          Elimina
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {praticheFiltered.length === 0 && (
              <div className="text-center py-8 text-gray-500">Nessuna pratica trovata</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================
// MARKETING AUTOMATION COMPONENT
// ============================================
const Marketing = () => {
  const [filters, setFilters] = useState({
    stato_civile: [],
    ha_figli: null,
    tessera_scaduta: null,
    tessera_in_scadenza_giorni: null,
    occupazione: [],
    stato_pratica: '',
    tipo_pratica: ''
  });
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const navigate = useNavigate();

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const statoCivileDropdown = document.getElementById('statoCivileDropdown');
      const occupazioneDropdown = document.getElementById('occupazioneDropdown');
      
      if (statoCivileDropdown && !event.target.closest('.relative')) {
        statoCivileDropdown.style.display = 'none';
      }
      if (occupazioneDropdown && !event.target.closest('.relative')) {
        occupazioneDropdown.style.display = 'none';
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchTemplates = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/marketing/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Errore caricamento template:', error);
    }
  };

  const handleFilter = async () => {
    const token = localStorage.getItem('token');
    
    // Prepara filtri
    const filterData = {};
    if (filters.stato_civile.length > 0) filterData.stato_civile = filters.stato_civile;
    if (filters.ha_figli !== null) filterData.ha_figli = filters.ha_figli;
    if (filters.tessera_scaduta !== null) filterData.tessera_scaduta = filters.tessera_scaduta;
    if (filters.tessera_in_scadenza_giorni) filterData.tessera_in_scadenza_giorni = parseInt(filters.tessera_in_scadenza_giorni);
    if (filters.occupazione.length > 0) filterData.occupazione = filters.occupazione;
    if (filters.stato_pratica) filterData.stato_pratica = filters.stato_pratica;
    if (filters.tipo_pratica) filterData.tipo_pratica = filters.tipo_pratica;

    try {
      const response = await fetch(`${API}/marketing/filter-clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(filterData)
      });

      if (response.ok) {
        const data = await response.json();
        setFilteredClients(data.clients);
        setSelectedClients(data.clients.map(c => c.id)); // Seleziona tutti per default
      } else {
        alert('Errore nel filtrare i clienti');
      }
    } catch (error) {
      console.error('Errore:', error);
      alert('Errore di connessione');
    }
  };

  const handleTemplateSelect = (template) => {
    setEmailSubject(template.subject);
    setEmailBody(template.body);
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const selectAll = () => {
    setSelectedClients(filteredClients.map(c => c.id));
  };

  const deselectAll = () => {
    setSelectedClients([]);
  };

  const handleSendWhatsApp = () => {
    if (selectedClients.length === 0) {
      alert('Seleziona almeno un cliente');
      return;
    }
    if (!emailBody) {
      alert('Inserisci il testo del messaggio');
      return;
    }

    // Genera link WhatsApp per ogni cliente selezionato
    const links = filteredClients
      .filter(c => selectedClients.includes(c.id))
      .map(client => {
        // Pulisci numero telefono (rimuovi spazi, trattini, parentesi)
        const cleanPhone = client.telefono.replace(/[\s\-\(\)]/g, '');
        // Aggiungi prefisso +39 se manca
        const phone = cleanPhone.startsWith('+') ? cleanPhone : `+39${cleanPhone}`;
        
        // Personalizza messaggio
        let message = emailBody
          .replace(/\{\{nome\}\}/g, client.nome)
          .replace(/\{\{cognome\}\}/g, client.cognome)
          .replace(/\{\{nome_completo\}\}/g, `${client.nome} ${client.cognome}`)
          .replace(/\{\{numero_tessera\}\}/g, client.numero_tessera)
          .replace(/\{\{data_scadenza\}\}/g, client.data_scadenza || 'N/A');
        
        return {
          client: `${client.nome} ${client.cognome}`,
          phone: client.telefono,
          link: `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
        };
      });

    setWhatsappLinks(links);
    setShowWhatsAppModal(true);
  };

  const handleSendEmails = async () => {
    if (selectedClients.length === 0) {
      alert('Seleziona almeno un cliente');
      return;
    }
    if (!emailSubject || !emailBody) {
      alert('Inserisci oggetto e corpo dell\'email');
      return;
    }

    if (!window.confirm(`Sei sicuro di voler inviare ${selectedClients.length} email?`)) {
      return;
    }

    setSending(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API}/marketing/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_ids: selectedClients,
          subject: emailSubject,
          body: emailBody
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Email inviate con successo!\n\nInviate: ${data.sent}\nFallite: ${data.failed}\nTotale: ${data.total}`);
        
        // Reset form
        setEmailSubject('');
        setEmailBody('');
        setFilteredClients([]);
        setSelectedClients([]);
      } else {
        alert('Errore nell\'invio delle email');
      }
    } catch (error) {
      console.error('Errore:', error);
      alert('Errore di connessione');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-blue-600 to-purple-700 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-800">📊 Marketing Automation</h1>
            <button
              onClick={() => navigate('/clients')}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              ← Torna ai Clienti
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Filtri */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-800">🔍 Filtri Clienti</h2>
            
            {/* Stato Civile */}
            <div className="mb-4 relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Stato Civile</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    const dropdown = document.getElementById('statoCivileDropdown');
                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-left bg-white hover:bg-gray-50 flex justify-between items-center"
                >
                  <span className="text-sm">
                    {filters.stato_civile.length === 0 ? 'Seleziona...' : `${filters.stato_civile.length} selezionati`}
                  </span>
                  <span>▼</span>
                </button>
                <div
                  id="statoCivileDropdown"
                  style={{display: 'none'}}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto"
                >
                  {['Celibe/Nubile', 'Coniugato/a', 'Divorziato/a', 'Vedovo/a', 'Separato/a'].map(stato => (
                    <label key={stato} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.stato_civile.includes(stato)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({...filters, stato_civile: [...filters.stato_civile, stato]});
                          } else {
                            setFilters({...filters, stato_civile: filters.stato_civile.filter(s => s !== stato)});
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">{stato}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Figli */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Figli</label>
              <select
                value={filters.ha_figli === null ? '' : filters.ha_figli}
                onChange={(e) => setFilters({...filters, ha_figli: e.target.value === '' ? null : e.target.value === 'true'})}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">Tutti</option>
                <option value="true">Con figli</option>
                <option value="false">Senza figli</option>
              </select>
            </div>

            {/* Occupazione */}
            <div className="mb-4 relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Occupazione</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    const dropdown = document.getElementById('occupazioneDropdown');
                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-left bg-white hover:bg-gray-50 flex justify-between items-center"
                >
                  <span className="text-sm">
                    {filters.occupazione.length === 0 ? 'Seleziona...' : `${filters.occupazione.length} selezionati`}
                  </span>
                  <span>▼</span>
                </button>
                <div
                  id="occupazioneDropdown"
                  style={{display: 'none'}}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-64 overflow-y-auto"
                >
                  {['Impiegato', 'Operaio', 'Artigiano', 'Commerciante', 'Libero Professionista', 'Insegnante', 'Infermiere', 'Medico', 'Avvocato', 'Ingegnere', 'Architetto', 'Pensionato', 'Casalinga/o', 'Studente', 'Disoccupato', 'Altro'].map(occ => (
                    <label key={occ} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.occupazione.includes(occ)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({...filters, occupazione: [...filters.occupazione, occ]});
                          } else {
                            setFilters({...filters, occupazione: filters.occupazione.filter(o => o !== occ)});
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">{occ}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Tessera */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Stato Tessera</label>
              <select
                value={filters.tessera_scaduta === null ? '' : filters.tessera_scaduta}
                onChange={(e) => setFilters({...filters, tessera_scaduta: e.target.value === '' ? null : e.target.value === 'true'})}
                className="w-full border border-gray-300 rounded px-3 py-2 mb-2"
              >
                <option value="">Tutte</option>
                <option value="false">Attive</option>
                <option value="true">Scadute</option>
              </select>
              
              <label className="block text-sm text-gray-600 mb-1">In scadenza entro (giorni):</label>
              <input
                type="number"
                value={filters.tessera_in_scadenza_giorni || ''}
                onChange={(e) => setFilters({...filters, tessera_in_scadenza_giorni: e.target.value})}
                placeholder="es. 30"
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>

            {/* Pratiche */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Stato Pratica</label>
              <select
                value={filters.stato_pratica}
                onChange={(e) => setFilters({...filters, stato_pratica: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">Tutti</option>
                <option value="In Lavorazione">In Lavorazione</option>
                <option value="Completata">Completata</option>
                <option value="Sospesa">Sospesa</option>
              </select>
            </div>

            <button
              onClick={handleFilter}
              className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 font-semibold"
            >
              🔍 Filtra Clienti
            </button>
          </div>

          {/* Risultati e Email */}
          <div className="lg:col-span-2 space-y-6">
            {/* Clienti Filtrati */}
            {filteredClients.length > 0 && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-800">
                    👥 Clienti Trovati ({filteredClients.length})
                  </h2>
                  <div className="space-x-2">
                    <button onClick={selectAll} className="text-sm text-blue-600 hover:underline">
                      Seleziona tutti
                    </button>
                    <button onClick={deselectAll} className="text-sm text-red-600 hover:underline">
                      Deseleziona tutti
                    </button>
                  </div>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {filteredClients.map(client => (
                    <label key={client.id} className="flex items-center p-3 hover:bg-gray-50 border-b">
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={() => toggleClientSelection(client.id)}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">{client.nome} {client.cognome}</div>
                        <div className="text-sm text-gray-600">{client.email} • {client.numero_tessera}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Editor Email */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">✉️ Componi Email</h2>
              
              {/* Template */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Template</label>
                <select
                  onChange={(e) => {
                    const template = templates.find(t => t.id === e.target.value);
                    if (template) handleTemplateSelect(template);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Seleziona un template...</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              {/* Oggetto */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Oggetto</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Oggetto dell'email"
                />
              </div>

              {/* Corpo */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Corpo Email
                  <span className="text-xs text-gray-500 ml-2">
                    (Usa variabili: nome, cognome, numero_tessera, data_scadenza tra doppie parentesi graffe per personalizzare)
                  </span>
                </label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={10}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Scrivi il testo dell'email..."
                />
              </div>

              {/* Azioni */}
              <div className="flex gap-4">
                <button
                  onClick={handleSendEmails}
                  disabled={sending || selectedClients.length === 0}
                  className={`flex-1 py-3 rounded font-semibold ${
                    sending || selectedClients.length === 0
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {sending ? '📤 Invio in corso...' : `📧 Invia Email (${selectedClients.length})`}
                </button>
                
                <button
                  onClick={handleSendWhatsApp}
                  disabled={selectedClients.length === 0}
                  className={`flex-1 py-3 rounded font-semibold ${
                    selectedClients.length === 0
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  💬 WhatsApp ({selectedClients.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">💬 Link WhatsApp Generati</h2>
                <button
                  onClick={() => setShowWhatsAppModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Clicca su ogni link per aprire WhatsApp con il messaggio pronto. Dovrai premere "Invia" manualmente.
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-3">
                {whatsappLinks.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-800">{item.client}</p>
                        <p className="text-sm text-gray-600">{item.phone}</p>
                      </div>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
                      >
                        💬 Apri WhatsApp
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="w-full py-3 bg-gray-600 text-white rounded hover:bg-gray-700 font-semibold"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// SCANNER REMOTO - PROTOTIPO DEMO
// ============================================

const ScannerRemotoQR = () => {
  const [sessionId, setSessionId] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, waiting, completed, expired
  const [timeLeft, setTimeLeft] = useState(180); // 3 minuti in secondi
  const pollingIntervalRef = React.useRef(null);
  const timeoutRef = React.useRef(null);
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const startSession = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/remote-session/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tipo: 'qr' })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        
        // Genera QR code URL usando API pubblica
        const mobileUrl = `${window.location.origin}/remote-qr/${data.session_id}`;
        setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(mobileUrl)}`);
        
        setStatus('waiting');
        setTimeLeft(180); // Reset timer
        startPolling(data.session_id);
        startTimer();
      }
    } catch (error) {
      console.error('Errore creazione session:', error);
      alert('Errore durante la creazione della session');
    } finally {
      setLoading(false);
    }
  };
  
  const startTimer = () => {
    const timerInterval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const startPolling = (sessId) => {
    // Pulisci interval precedenti
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API}/remote-session/${sessId}/status`);
        
        if (response.ok) {
          const data = await response.json();
          console.log('📡 Polling response:', data); // Debug log
          
          if (data.status === 'completed' && data.data) {
            console.log('✅ Status completed! Data received:', data.data); // Debug log
            clearInterval(pollingIntervalRef.current);
            clearTimeout(timeoutRef.current);
            setStatus('completed');
            
            // Trova cliente tramite QR code e apri SCHEDA completa
            const qrCode = data.data.qr_code;
            console.log('🆔 QR Code ricevuto:', qrCode); // Debug log
            console.log('🔀 Navigating to:', `/client/${qrCode}`); // Debug log
            
            // Aspetta un attimo per assicurarsi che lo stato sia aggiornato
            setTimeout(() => {
              console.log('🚀 Executing navigation...'); // Debug log
              // Naviga alla scheda cliente completa (non tessera)
              navigate(`/client/${qrCode}`);
            }, 100);
          }
        } else if (response.status === 410) {
          clearInterval(pollingIntervalRef.current);
          clearTimeout(timeoutRef.current);
          setStatus('expired');
        }
      } catch (error) {
        console.error('❌ Errore polling:', error);
      }
    }, 1000); // Controlla ogni secondo
    
    // Stop dopo 3 minuti
    timeoutRef.current = setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      setStatus('expired');
    }, 180000);
  };
  
  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate('/')}
            className="mb-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            ← Torna alla Home
          </button>
          
          <h1 className="text-3xl font-bold text-gray-800 mb-2">📱 Scanner Remoto QR</h1>
          <p className="text-gray-600 mb-6">Scansiona tessere cliente dal cellulare</p>
          
          {status === 'idle' && (
            <div className="text-center">
              <p className="text-gray-700 mb-6">Clicca il pulsante per generare un QR code da scansionare con il cellulare</p>
              <button
                onClick={startSession}
                disabled={loading}
                className="px-8 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-bold text-lg"
              >
                {loading ? '⏳ Generazione...' : '📱 Genera QR Code'}
              </button>
            </div>
          )}
          
          {status === 'waiting' && qrCodeUrl && (
            <div className="text-center">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 font-semibold">⏱️ In attesa di scansione dal cellulare...</p>
                <p className="text-yellow-600 text-sm mt-1">Tempo rimanente: <span className="font-bold text-lg">{formatTime(timeLeft)}</span></p>
                <div className="mt-2 bg-yellow-200 rounded-full h-2">
                  <div 
                    className="bg-yellow-600 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${(timeLeft / 180) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="bg-white border-4 border-purple-600 rounded-lg p-6 inline-block">
                <img src={qrCodeUrl} alt="QR Code" className="w-72 h-72" />
              </div>
              
              <p className="text-gray-600 mt-4 text-sm">
                1. Apri la fotocamera del cellulare<br />
                2. Inquadra questo QR code<br />
                3. Scansiona la tessera del cliente<br />
                4. La scheda si aprirà automaticamente qui
              </p>
              
              <button
                onClick={() => {
                  setStatus('idle');
                  setSessionId(null);
                  setQrCodeUrl(null);
                }}
                className="mt-6 px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Annulla
              </button>
            </div>
          )}
          
          {status === 'completed' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <p className="text-green-800 font-bold text-xl mb-2">✅ Scansione Completata!</p>
              <p className="text-green-600">Apertura scheda cliente...</p>
            </div>
          )}
          
          {status === 'expired' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-800 font-bold text-xl mb-4">⏰ Session Scaduta</p>
              <button
                onClick={startSession}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
              >
                🔄 Genera Nuovo QR Code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Pagina Mobile per scansionare QR tessera
const RemoteQRScanner = () => {
  const { sessionId } = useParams();
  const [scanning, setScanning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [useCamera, setUseCamera] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const scannerRef = React.useRef(null);
  
  // Avvia scanner fotocamera
  const startCameraScanner = async () => {
    setUseCamera(true);
    setCameraError('');
    
    try {
      // Importa dinamicamente html5-qrcode
      const { Html5Qrcode } = await import('html5-qrcode');
      
      setTimeout(async () => {
        try {
          const html5QrCode = new Html5Qrcode("remote-qr-reader");
          scannerRef.current = html5QrCode;
          
          await html5QrCode.start(
            { facingMode: "environment" }, // Fotocamera posteriore
            {
              fps: 10,
              qrbox: { width: 250, height: 250 }
            },
            async (decodedText) => {
              console.log('📷 QR Code scansionato:', decodedText);
              
              // Estrai client ID dall'URL scansionato
              let clientId = decodedText;
              
              // Gestisci sia /card/ che /qr/ nelle tessere
              if (decodedText.includes('/card/')) {
                clientId = decodedText.split('/card/').pop();
              } else if (decodedText.includes('/qr/')) {
                clientId = decodedText.split('/qr/').pop();
              }
              
              // Rimuovi eventuali parametri query o frammenti
              clientId = clientId.split('?')[0].split('#')[0];
              
              console.log('🆔 Client ID estratto:', clientId);
              
              // Ferma scanner
              if (scannerRef.current) {
                await scannerRef.current.stop();
              }
              
              // Invia al backend
              await handleQRScan(clientId);
            }
          );
        } catch (err) {
          console.error('Errore avvio scanner:', err);
          setCameraError('Errore accesso fotocamera. Usa input manuale.');
          setUseCamera(false);
        }
      }, 100);
    } catch (err) {
      console.error('Errore import html5-qrcode:', err);
      setCameraError('Errore caricamento scanner. Usa input manuale.');
      setUseCamera(false);
    }
  };
  
  // Ferma scanner
  const stopCameraScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (err) {
        console.error('Errore stop scanner:', err);
      }
    }
    setUseCamera(false);
  };
  
  // Cleanup quando componente smonta
  React.useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(err => console.error(err));
      }
    };
  }, []);
  
  const handleQRScan = async (qrCode) => {
    if (scanning || completed || !qrCode.trim()) return;
    
    setScanning(true);
    
    try {
      const response = await fetch(`${API}/remote-session/${sessionId}/qr-scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ qr_code: qrCode.trim() })
      });
      
      if (response.ok) {
        setCompleted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Errore durante la scansione');
      }
    } catch (err) {
      setError('Errore di connessione');
    } finally {
      setScanning(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-indigo-700 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-2xl p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">📱 Scanner Remoto QR</h1>
          <p className="text-gray-600 text-sm text-center mb-6">Scansiona tessera del cliente</p>
          
          {!completed && !error && !useCamera && (
            <div>
              {/* Pulsante Scanner Fotocamera */}
              <button
                onClick={startCameraScanner}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-bold text-lg mb-4 shadow-lg transform transition hover:scale-105"
              >
                📷 Scansiona QR con Fotocamera
              </button>
              
              {cameraError && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                  <p className="text-orange-800 text-sm">{cameraError}</p>
                </div>
              )}
              
              {/* Divider */}
              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-3 text-gray-500 text-sm">oppure</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
              
              {/* Input manuale */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 font-semibold text-sm mb-2">✍️ Inserimento Manuale:</p>
                <p className="text-blue-700 text-xs">Se la fotocamera non funziona, puoi inserire l'ID manualmente</p>
              </div>
              
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ID Tessera Cliente:
              </label>
              <input
                type="text"
                placeholder="es: abc123xyz456"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg mb-4 text-lg focus:border-purple-500 focus:outline-none"
              />
              
              <button
                onClick={() => handleQRScan(inputValue)}
                disabled={scanning || !inputValue.trim()}
                className={`w-full py-3 rounded-lg font-bold text-lg ${
                  scanning || !inputValue.trim()
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700'
                } text-white`}
              >
                {scanning ? '⏳ Invio...' : '✅ Invia al PC'}
              </button>
              
              <p className="text-xs text-gray-400 text-center mt-4">
                Session: {sessionId?.substring(0, 8)}...
              </p>
            </div>
          )}
          
          {/* Scanner Fotocamera Attivo */}
          {!completed && !error && useCamera && (
            <div>
              <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4 mb-4">
                <p className="text-purple-800 font-semibold text-center mb-2">📷 Inquadra il QR Code</p>
                <p className="text-purple-600 text-xs text-center">Posiziona il QR code della tessera al centro</p>
              </div>
              
              {/* Contenitore Scanner */}
              <div id="remote-qr-reader" className="mb-4 rounded-lg overflow-hidden"></div>
              
              <button
                onClick={stopCameraScanner}
                className="w-full py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-semibold"
              >
                ❌ Chiudi Scanner
              </button>
            </div>
          )}
          
          {completed && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <p className="text-6xl mb-4">✅</p>
              <p className="text-green-800 font-bold text-2xl mb-2">Inviato!</p>
              <p className="text-green-600 text-lg">Controlla il PC/iPad</p>
              <p className="text-green-500 text-sm mt-4">La scheda cliente si aprirà automaticamente</p>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-5xl mb-4">❌</p>
              <p className="text-red-800 font-bold text-xl mb-2">Errore</p>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => {
                  setError('');
                  setInputValue('');
                }}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
              >
                🔄 Riprova
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Scanner Remoto OCR - PC genera QR per upload foto da mobile
const ScannerRemotoOCR = () => {
  const [sessionId, setSessionId] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('idle');
  const [timeLeft, setTimeLeft] = useState(180);
  const [ocrResult, setOcrResult] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const startSession = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/remote-session/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tipo: 'ocr' })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        
        const mobileUrl = `${window.location.origin}/remote-ocr/${data.session_id}`;
        setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(mobileUrl)}`);
        
        setStatus('waiting');
        setTimeLeft(180);
        startPolling(data.session_id);
        startTimer();
      }
    } catch (error) {
      alert('Errore durante la creazione della session');
    } finally {
      setLoading(false);
    }
  };
  
  const startTimer = () => {
    const timerInterval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const startPolling = (sessId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API}/remote-session/${sessId}/status`);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.status === 'completed' && data.data) {
            clearInterval(interval);
            setStatus('completed');
            setOcrResult(data.data);
          }
        } else if (response.status === 410) {
          clearInterval(interval);
          setStatus('expired');
        }
      } catch (error) {
        console.error('Errore polling:', error);
      }
    }, 1000);
    
    setTimeout(() => {
      clearInterval(interval);
      if (status === 'waiting') {
        setStatus('expired');
      }
    }, 180000);
  };
  
  const handleCreateClient = () => {
    if (!ocrResult || !ocrResult.dati_estratti) return;
    
    const queryParams = new URLSearchParams();
    const dati = ocrResult.dati_estratti;
    
    console.log('📋 OCR Remote - Data to transfer:', dati);
    
    // Funzione helper per convertire date da formato DD.MM.YYYY o DD/MM/YYYY a YYYY-MM-DD
    const convertDate = (dateStr) => {
      if (!dateStr) return '';
      console.log('🔄 Converting date:', dateStr);
      // Supporta sia . che / che -
      const parts = dateStr.split(/[./-]/);
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const converted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        console.log('✅ Date converted to:', converted);
        return converted;
      }
      console.log('⚠️ Date format not recognized, returning as-is');
      return dateStr;
    };
    
    // Dati anagrafici
    if (dati.nome) queryParams.set('nome', dati.nome);
    if (dati.cognome) queryParams.set('cognome', dati.cognome);
    if (dati.codice_fiscale) queryParams.set('codice_fiscale', dati.codice_fiscale);
    // Normalizza sesso per compatibilità con il form
    if (dati.sesso) {
      const sessoValue = dati.sesso;
      if (sessoValue === 'M' || sessoValue === 'Maschio' || sessoValue === 'MASCHIO' || sessoValue === 'm') {
        queryParams.set('sesso', 'M');
      } else if (sessoValue === 'F' || sessoValue === 'Femmina' || sessoValue === 'FEMMINA' || sessoValue === 'f') {
        queryParams.set('sesso', 'F');
      } else {
        queryParams.set('sesso', 'Altro');
      }
    }
    
    // Date (converti formato)
    if (dati.data_nascita) queryParams.set('data_nascita', convertDate(dati.data_nascita));
    if (dati.luogo_nascita) queryParams.set('luogo_nascita', dati.luogo_nascita);
    
    // Residenza
    if (dati.indirizzo || dati.indirizzo_residenza) {
      queryParams.set('indirizzo', dati.indirizzo || dati.indirizzo_residenza);
    }
    if (dati.citta) queryParams.set('citta', dati.citta);
    if (dati.cap) queryParams.set('cap', dati.cap);
    if (dati.provincia) queryParams.set('provincia', dati.provincia);
    
    // Documento
    if (ocrResult.tipo_documento) {
      queryParams.set('documento_tipo', ocrResult.tipo_documento);
    }
    if (dati.numero_documento) {
      queryParams.set('documento_numero', dati.numero_documento);
    }
    if (dati.data_rilascio || dati.data_emissione) {
      queryParams.set('documento_data_emissione', convertDate(dati.data_rilascio || dati.data_emissione));
    }
    if (dati.data_scadenza) {
      queryParams.set('documento_data_scadenza', convertDate(dati.data_scadenza));
    }
    
    const finalUrl = `/register?${queryParams.toString()}`;
    console.log('🚀 OCR Remote - Navigating to:', finalUrl);
    console.log('📦 OCR Remote - Query params:', Object.fromEntries(queryParams));
    
    navigate(finalUrl);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate('/')}
            className="mb-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            ← Torna alla Home
          </button>
          
          <h1 className="text-3xl font-bold text-gray-800 mb-2">📄 Scanner Remoto Documenti OCR</h1>
          <p className="text-gray-600 mb-6">Scansiona documenti dal cellulare con analisi automatica</p>
          
          {status === 'idle' && (
            <div className="text-center">
              <p className="text-gray-700 mb-6">Genera un QR code per scansionare documenti dal cellulare</p>
              <button
                onClick={startSession}
                disabled={loading}
                className="px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-bold text-lg"
              >
                {loading ? '⏳ Generazione...' : '📱 Genera QR Code OCR'}
              </button>
            </div>
          )}
          
          {status === 'waiting' && qrCodeUrl && (
            <div className="text-center">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
                <p className="text-teal-800 font-semibold">📸 In attesa di foto da cellulare...</p>
                <p className="text-teal-600 text-sm mt-1">Tempo rimanente: <span className="font-bold text-lg">{formatTime(timeLeft)}</span></p>
                <div className="mt-2 bg-teal-200 rounded-full h-2">
                  <div 
                    className="bg-teal-600 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${(timeLeft / 180) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="bg-white border-4 border-teal-600 rounded-lg p-6 inline-block">
                <img src={qrCodeUrl} alt="QR Code" className="w-72 h-72" />
              </div>
              
              <p className="text-gray-600 mt-4 text-sm">
                1. Apri la fotocamera del cellulare<br />
                2. Inquadra questo QR code<br />
                3. Scatta foto del documento<br />
                4. I dati appariranno automaticamente qui
              </p>
            </div>
          )}
          
          {status === 'completed' && ocrResult && (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <p className="text-green-800 font-bold text-xl mb-2">✅ Documento Ricevuto!</p>
                <p className="text-green-600">Tipo: {ocrResult.tipo_documento}</p>
              </div>
              
              <div className="bg-gray-50 border rounded-lg p-4 mb-4">
                <h3 className="font-bold text-gray-800 mb-3">📋 Dati Estratti:</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(ocrResult.dati_estratti || {}).map(([key, value]) => (
                    <div key={key} className="bg-white p-3 rounded">
                      <p className="text-xs text-gray-600 uppercase">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium">{value || '-'}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <button
                onClick={handleCreateClient}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                👤 Crea Cliente con Questi Dati
              </button>
            </div>
          )}
          
          {status === 'expired' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-800 font-bold text-xl mb-4">⏰ Session Scaduta</p>
              <button
                onClick={startSession}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-semibold"
              >
                🔄 Genera Nuovo QR Code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Pagina Mobile per OCR - scatta foto documento
const RemoteOCRScanner = () => {
  const { sessionId } = useParams();
  const [uploading, setUploading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = React.useRef(null);
  
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    console.log('📁 File selezionato:', file.name, file.size, 'bytes');
    setUploading(true);
    setError('');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('📤 Invio file al backend...');
      const response = await fetch(`${API}/remote-session/${sessionId}/ocr-scan`, {
        method: 'POST',
        body: formData
      });
      
      console.log('📥 Response status:', response.status);
      
      if (response.ok) {
        console.log('✅ Upload completato!');
        setCompleted(true);
      } else {
        const data = await response.json();
        console.error('❌ Errore response:', data);
        setError(data.detail || 'Errore durante l\'upload');
      }
    } catch (err) {
      console.error('❌ Errore upload:', err);
      setError('Errore di connessione. Riprova.');
    } finally {
      setUploading(false);
    }
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 to-cyan-700 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-2xl p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4 text-center">📄 Scansiona Documento</h1>
          
          {!completed && !error && (
            <div>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
                <p className="text-teal-800 font-semibold text-sm mb-2">📸 Come fare:</p>
                <ol className="text-teal-700 text-xs space-y-1">
                  <li>1. Click sul pulsante verde sotto</li>
                  <li>2. Scegli "Fotocamera" o "Galleria"</li>
                  <li>3. Scatta o seleziona foto del documento</li>
                  <li>4. Attendi invio automatico</li>
                </ol>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <button
                onClick={triggerFileInput}
                disabled={uploading}
                className={`w-full py-4 rounded-lg font-bold text-lg ${
                  uploading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-teal-600 hover:bg-teal-700'
                } text-white`}
              >
                {uploading ? '⏳ Invio in corso...' : '📷 Scatta o Carica Foto'}
              </button>
              
              <p className="text-xs text-gray-400 text-center mt-4">
                Session: {sessionId?.substring(0, 8)}...
              </p>
            </div>
          )}
          
          {completed && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <p className="text-4xl mb-4">✅</p>
              <p className="text-green-800 font-bold text-xl mb-2">Documento Inviato!</p>
              <p className="text-green-600">Controlla il PC per vedere i dati estratti</p>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-4xl mb-4">❌</p>
              <p className="text-red-800 font-bold mb-2">Errore</p>
              <p className="text-red-600">{error}</p>
              <button
                onClick={() => setError('')}
                className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                🔄 Riprova
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SCANSIONE DOCUMENTI CON OCR
// ============================================

const ScansioneDocumenti = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [datiEstratti, setDatiEstratti] = useState({});
  const [clienteTrovato, setClienteTrovato] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const { token } = useAuth();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setScanResult(null);
      setDatiEstratti({});
      setClienteTrovato(null);
    }
  };

  const handleScan = async () => {
    if (!selectedFile) {
      alert('Seleziona un file prima di scansionare');
      return;
    }

    setScanning(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch(`${API}/documenti/scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setScanResult(data);
        setDatiEstratti(data.dati_estratti);
        setClienteTrovato(data.cliente_trovato);
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail}`);
      }
    } catch (error) {
      console.error('Errore scansione:', error);
      alert('Errore durante la scansione del documento');
    } finally {
      setScanning(false);
    }
  };

  const handleUpdateData = (field, value) => {
    setDatiEstratti(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveVerified = async () => {
    if (!scanResult) return;

    try {
      const response = await fetch(`${API}/documenti/scansionati/${scanResult.documento_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          dati_estratti: datiEstratti,
          verificato: true,
          cliente_id: clienteTrovato?.id || null
        })
      });

      if (response.ok) {
        alert('Dati verificati e salvati con successo!');
        // Reset
        setSelectedFile(null);
        setPreviewUrl(null);
        setScanResult(null);
        setDatiEstratti({});
        setClienteTrovato(null);
        setEditMode(false);
      } else {
        alert('Errore durante il salvataggio');
      }
    } catch (error) {
      console.error('Errore salvataggio:', error);
      alert('Errore durante il salvataggio');
    }
  };

  const handleCreateClientFromScan = () => {
    // Naviga al form di registrazione con dati pre-compilati
    const queryParams = new URLSearchParams();
    
    // Funzione helper per convertire date da formato DD.MM.YYYY o DD/MM/YYYY a YYYY-MM-DD
    const convertDate = (dateStr) => {
      if (!dateStr) return '';
      console.log('🔄 Converting date:', dateStr);
      // Supporta sia . che / che -
      const parts = dateStr.split(/[./-]/);
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const converted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        console.log('✅ Date converted to:', converted);
        return converted;
      }
      console.log('⚠️ Date format not recognized, returning as-is');
      return dateStr;
    };
    
    // IMPORTANTE: Passa il documento_id per collegarlo al cliente
    if (scanResult?.documento_id) {
      queryParams.set('documento_scansionato_id', scanResult.documento_id);
    }
    
    console.log('📋 OCR Data to transfer:', datiEstratti);
    
    // Dati anagrafici
    if (datiEstratti.nome) queryParams.set('nome', datiEstratti.nome);
    if (datiEstratti.cognome) queryParams.set('cognome', datiEstratti.cognome);
    if (datiEstratti.codice_fiscale) queryParams.set('codice_fiscale', datiEstratti.codice_fiscale);
    
    // Converti data nascita
    if (datiEstratti.data_nascita) {
      const converted = convertDate(datiEstratti.data_nascita);
      queryParams.set('data_nascita', converted);
    }
    
    if (datiEstratti.luogo_nascita) queryParams.set('luogo_nascita', datiEstratti.luogo_nascita);
    // Normalizza sesso per compatibilità con il form
    if (datiEstratti.sesso) {
      const sessoValue = datiEstratti.sesso;
      if (sessoValue === 'M' || sessoValue === 'Maschio' || sessoValue === 'MASCHIO' || sessoValue === 'm') {
        queryParams.set('sesso', 'M');
      } else if (sessoValue === 'F' || sessoValue === 'Femmina' || sessoValue === 'FEMMINA' || sessoValue === 'f') {
        queryParams.set('sesso', 'F');
      } else {
        queryParams.set('sesso', 'Altro');
      }
    }
    
    // Indirizzo e residenza
    if (datiEstratti.indirizzo || datiEstratti.indirizzo_residenza) {
      queryParams.set('indirizzo', datiEstratti.indirizzo || datiEstratti.indirizzo_residenza);
    }
    if (datiEstratti.citta) queryParams.set('citta', datiEstratti.citta);
    if (datiEstratti.cap) queryParams.set('cap', datiEstratti.cap);
    if (datiEstratti.provincia) queryParams.set('provincia', datiEstratti.provincia);
    
    // Documento
    if (datiEstratti.numero_documento || datiEstratti.numero_patente) {
      queryParams.set('documento_numero', datiEstratti.numero_documento || datiEstratti.numero_patente);
    }
    
    // Converti date documento
    if (datiEstratti.data_rilascio || datiEstratti.data_emissione) {
      const dateValue = datiEstratti.data_rilascio || datiEstratti.data_emissione;
      const converted = convertDate(dateValue);
      queryParams.set('documento_data_emissione', converted);
    }
    
    if (datiEstratti.data_scadenza) {
      const converted = convertDate(datiEstratti.data_scadenza);
      queryParams.set('documento_data_scadenza', converted);
    }
    
    // Passa anche il tipo documento dal risultato scan
    if (scanResult?.tipo_documento) {
      queryParams.set('documento_tipo', scanResult.tipo_documento);
    }
    
    const finalUrl = `/register?${queryParams.toString()}`;
    console.log('🚀 Navigating to:', finalUrl);
    console.log('📦 Query params object:', Object.fromEntries(queryParams));
    
    navigate(finalUrl);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">🔍 Scansione Documenti OCR</h1>
              <p className="text-gray-600 mt-2">Carica e analizza automaticamente documenti con intelligenza artificiale</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              ← Torna alla Home
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sezione Upload e Anteprima */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">📤 Carica Documento</h2>
            
            <div className="mb-6">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500 mt-2">
                Supportati: Immagini (JPG, PNG) e PDF
              </p>
            </div>

            {previewUrl && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-700 mb-2">Anteprima:</h3>
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="w-full h-auto max-h-96 object-contain border rounded"
                />
              </div>
            )}

            <button
              onClick={handleScan}
              disabled={!selectedFile || scanning}
              className={`w-full py-3 rounded-lg font-semibold text-white transition ${
                !selectedFile || scanning
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {scanning ? '🔄 Scansione in corso...' : '🔍 Scansiona Documento'}
            </button>
          </div>

          {/* Sezione Risultati */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">📋 Dati Estratti</h2>

            {!scanResult && (
              <div className="text-center text-gray-500 py-12">
                <p className="text-lg">Nessun documento scansionato</p>
                <p className="text-sm mt-2">Carica e scansiona un documento per vedere i risultati</p>
              </div>
            )}

            {scanResult && (
              <div className="space-y-4">
                {/* Tipo Documento */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-blue-800">Tipo Documento Riconosciuto:</p>
                  <p className="text-lg font-bold text-blue-900">{scanResult.tipo_documento}</p>
                </div>

                {/* Cliente Trovato */}
                {clienteTrovato && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-sm font-semibold text-green-800 mb-2">✅ Cliente Trovato:</p>
                    <p className="text-lg font-bold text-green-900">
                      {clienteTrovato.nome} {clienteTrovato.cognome}
                    </p>
                    <p className="text-sm text-green-700">CF: {clienteTrovato.codice_fiscale}</p>
                    <button
                      onClick={() => navigate(`/client/${clienteTrovato.id}`)}
                      className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                      Vedi Cliente
                    </button>
                  </div>
                )}

                {/* Dati Estratti */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-700">Dati Estratti:</h3>
                    <button
                      onClick={() => setEditMode(!editMode)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {editMode ? '💾 Salva Modifiche' : '✏️ Modifica'}
                    </button>
                  </div>

                  {Object.keys(datiEstratti).length === 0 ? (
                    <p className="text-gray-500 text-sm">Nessun dato estratto</p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(datiEstratti).map(([key, value]) => (
                        <div key={key} className="flex flex-col">
                          <label className="text-xs font-semibold text-gray-600 uppercase">
                            {key.replace(/_/g, ' ')}
                          </label>
                          {editMode ? (
                            <input
                              type="text"
                              value={value || ''}
                              onChange={(e) => handleUpdateData(key, e.target.value)}
                              className="mt-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="mt-1 px-3 py-2 bg-gray-50 rounded">{value || '-'}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Azioni */}
                <div className="space-y-2">
                  <button
                    onClick={handleSaveVerified}
                    className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                  >
                    ✅ Verifica e Salva
                  </button>

                  {!clienteTrovato && Object.keys(datiEstratti).length > 0 && (
                    <button
                      onClick={handleCreateClientFromScan}
                      className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                    >
                      👤 Crea Nuovo Cliente con Questi Dati
                    </button>
                  )}
                </div>

                {/* Testo Completo */}
                {scanResult.testo_completo && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-2">📄 Testo Completo Estratto:</h3>
                    <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 max-h-48 overflow-y-auto whitespace-pre-wrap">
                      {scanResult.testo_completo}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sezione Documenti Scansionati (Storico) */}
        <DocumentiScansionatiList />
      </div>
    </div>
  );
};

// Componente per lista documenti scansionati
const DocumentiScansionatiList = () => {
  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDocumenti();
  }, []);

  const handleShowDetails = (doc) => {
    setSelectedDoc(doc);
    setShowModal(true);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento scansionato?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/documenti/scansionati/${docId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Documento eliminato con successo!');
        fetchDocumenti(); // Ricarica lista
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail}`);
      }
    } catch (error) {
      console.error('Errore eliminazione:', error);
      alert('Errore durante l\'eliminazione del documento');
    }
  };

  const fetchDocumenti = async () => {
    try {
      const response = await fetch(`${API}/documenti/scansionati`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDocumenti(data.documenti);
      }
    } catch (error) {
      console.error('Errore caricamento documenti:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Caricamento...</div>;
  }

  if (documenti.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">📁 Documenti Scansionati Recenti</h2>
      
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Data</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Tipo</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Cliente</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Verificato</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {documenti.slice(0, 10).map((doc) => (
              <tr key={doc.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm">
                  {new Date(doc.data_scansione).toLocaleDateString('it-IT')}
                </td>
                <td className="px-4 py-2 text-sm">{doc.tipo_documento}</td>
                <td className="px-4 py-2 text-sm">
                  {doc.cliente_id ? (
                    <button
                      onClick={() => navigate(`/client/${doc.cliente_id}`)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Vedi Cliente
                    </button>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm">
                  {doc.verificato ? (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">✓ Verificato</span>
                  ) : (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">⚠ Da verificare</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleShowDetails(doc)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
                    >
                      👁️ Dettagli
                    </button>
                    <button 
                      onClick={() => handleDelete(doc.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-semibold"
                    >
                      🗑️ Elimina
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Dettagli Documento */}
      {showModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">📄 Dettagli Documento</h2>
                  <p className="text-blue-100 text-sm mt-1">
                    Scansionato il {new Date(selectedDoc.data_scansione).toLocaleDateString('it-IT')} alle{' '}
                    {new Date(selectedDoc.data_scansione).toLocaleTimeString('it-IT')}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:text-gray-200 text-3xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Tipo Documento */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-blue-800 mb-1">Tipo Documento:</p>
                <p className="text-xl font-bold text-blue-900">{selectedDoc.tipo_documento}</p>
              </div>

              {/* Cliente Associato */}
              {selectedDoc.cliente_id && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm font-semibold text-green-800 mb-2">✅ Cliente Associato</p>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      navigate(`/client/${selectedDoc.cliente_id}`);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
                  >
                    Vedi Cliente
                  </button>
                </div>
              )}

              {/* Stato Verifica */}
              <div className={`p-4 rounded-lg border ${
                selectedDoc.verificato 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <p className="text-sm font-semibold mb-1">
                  {selectedDoc.verificato ? '✅ Documento Verificato' : '⚠️ Documento da Verificare'}
                </p>
                <p className="text-xs text-gray-600">
                  Scansionato da: <span className="font-semibold">{selectedDoc.scansionato_da}</span>
                </p>
              </div>

              {/* Dati Estratti */}
              <div className="border rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Dati Estratti:</h3>
                
                {selectedDoc.dati_estratti && Object.keys(selectedDoc.dati_estratti).length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(selectedDoc.dati_estratti).map(([key, value]) => {
                      // Converti il valore in stringa se è un oggetto
                      let displayValue = '-';
                      if (value !== null && value !== undefined) {
                        if (typeof value === 'object') {
                          displayValue = JSON.stringify(value, null, 2);
                        } else {
                          displayValue = String(value);
                        }
                      }
                      
                      return (
                        <div key={key} className="bg-gray-50 p-3 rounded">
                          <p className="text-xs font-semibold text-gray-600 uppercase mb-1">
                            {key.replace(/_/g, ' ')}
                          </p>
                          <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">{displayValue}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm italic">Nessun dato estratto</p>
                )}
              </div>

              {/* Testo Completo */}
              {selectedDoc.testo_completo && (
                <div className="border rounded-lg p-4">
                  <h3 className="text-lg font-bold text-gray-800 mb-3">📝 Testo Completo Estratto:</h3>
                  <div className="bg-gray-50 p-4 rounded text-sm text-gray-700 max-h-64 overflow-y-auto whitespace-pre-wrap font-mono">
                    {selectedDoc.testo_completo}
                  </div>
                </div>
              )}

              {/* Note */}
              {selectedDoc.note && (
                <div className="border rounded-lg p-4">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">💬 Note:</h3>
                  <p className="text-sm text-gray-700">{selectedDoc.note}</p>
                </div>
              )}

              {/* Info File */}
              <div className="bg-gray-100 p-4 rounded-lg">
                <h3 className="text-sm font-bold text-gray-700 mb-2">📁 Informazioni File:</h3>
                <div className="text-xs text-gray-600 space-y-1">
                  <p><span className="font-semibold">Nome File:</span> {selectedDoc.nome_file}</p>
                  <p><span className="font-semibold">ID Documento:</span> {selectedDoc.id}</p>
                  {selectedDoc.pratica_id && (
                    <p><span className="font-semibold">ID Pratica:</span> {selectedDoc.pratica_id}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-4 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 font-semibold"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/t/:shortId" element={<CardPublic />} />
          <Route path="/upload/:uploadToken" element={<UploadDocumenti />} />
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/register" element={<ProtectedRoute><Register /></ProtectedRoute>} />
          <Route path="/success/:clientId" element={<ProtectedRoute><Success /></ProtectedRoute>} />
          <Route path="/card/:clientId" element={<Card />} />
          <Route path="/qr/:clientId" element={<ProtectedRoute><QRRedirect /></ProtectedRoute>} />
          <Route path="/client/:clientId" element={<ProtectedRoute><ClientDetails /></ProtectedRoute>} />
          <Route path="/client/:clientId/edit" element={<ProtectedRoute><EditClient /></ProtectedRoute>} />
          <Route path="/scan" element={<ProtectedRoute><Scanner /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><ClientsList /></ProtectedRoute>} />
          <Route path="/pratiche" element={<RoleProtectedRoute allowedRoles={['admin', 'operatore_completo']}><GestionePratiche /></RoleProtectedRoute>} />
          <Route path="/marketing" element={<RoleProtectedRoute allowedRoles={['admin']}><Marketing /></RoleProtectedRoute>} />
          <Route path="/scansione" element={<ProtectedRoute><ScansioneDocumenti /></ProtectedRoute>} />
          <Route path="/scanner-remoto-qr" element={<ProtectedRoute><ScannerRemotoQR /></ProtectedRoute>} />
          <Route path="/remote-qr/:sessionId" element={<RemoteQRScanner />} />
          <Route path="/scanner-remoto-ocr" element={<ProtectedRoute><ScannerRemotoOCR /></ProtectedRoute>} />
          <Route path="/remote-ocr/:sessionId" element={<RemoteOCRScanner />} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
